import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AgentLayout } from "@/components/AgentLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { TierBadge } from "@/components/TierBadge";
import {
  Suitcase, Users, Star, Plus, Trash, CheckCircle, EyeSlash, CircleNotch, MapPin,
  PencilSimple, DotsThreeVertical, Key, UserMinus, UserPlus, Copy, ChartLineUp, Trophy,
  Storefront, ArrowSquareOut, Eye, Gift, Clock, Lifebuoy, Prohibit, Play, DownloadSimple,
  ArrowsClockwise, Broom, CaretDown, CaretRight, FilePdf, EnvelopeSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { AgentStatusBadge, AGENT_STATUS_META } from "@/components/AgentStatusBadge";
import { SUPPLIER_CATEGORIES, CATEGORY_BY_VALUE } from "@/lib/supplierCategories";
import { AdminOverviewCards } from "@/components/AdminOverviewCards";
import { StatusCorrectionDialog } from "@/components/StatusCorrectionDialog";


const STATUS_STYLES = {
  Draft: "bg-white/5 text-white/60 border-white/10",
  Sent: "bg-blue-500/10 text-blue-300 border-blue-400/30",
  Approved: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  "Payment Authorization Received": "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  "Payment Link Sent": "bg-purple-500/10 text-purple-300 border-purple-400/40",
  "Deposit Confirmed": "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  "Deposit Paid": "bg-emerald-500/10 text-emerald-300 border-emerald-400/30",
  "Paid in Full": "bg-emerald-500/20 text-emerald-200 border-emerald-400/50",
  "Payment Received": "bg-emerald-500/10 text-emerald-300 border-emerald-400/30",
  Booked: "bg-green-500/15 text-green-300 border-green-400/40",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.Draft;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${cls}`}>
      {status}
    </span>
  );
}

function ReviewStatusBadge({ status }) {
  const map = {
    pending: "bg-amber-500/10 text-amber-300 border-amber-400/30",
    approved: "bg-green-500/15 text-green-300 border-green-400/40",
    hidden: "bg-white/5 text-white/50 border-white/10",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${map[status] || map.pending}`}>
      {status}
    </span>
  );
}

function Stars({ rating }) {
  return (
    <div className="flex items-center gap-0.5 text-[#D4AF37]">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={14} weight={n <= rating ? "fill" : "regular"} />
      ))}
    </div>
  );
}

function formatMoney(n) {
  if (n == null || isNaN(n)) return "$0";
  return "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

const MONTH_OPTIONS = [
  { value: "", label: "All months" },
  { value: "1", label: "January" }, { value: "2", label: "February" }, { value: "3", label: "March" },
  { value: "4", label: "April" }, { value: "5", label: "May" }, { value: "6", label: "June" },
  { value: "7", label: "July" }, { value: "8", label: "August" }, { value: "9", label: "September" },
  { value: "10", label: "October" }, { value: "11", label: "November" }, { value: "12", label: "December" },
];

const COMMISSION_STATUS_PILL = {
  "Link Added": "bg-white/5 text-white/60 border-white/10",
  "Sent to Client": "bg-blue-500/10 text-blue-300 border-blue-400/30",
  "Paid by Client": "bg-emerald-500/10 text-emerald-300 border-emerald-400/40",
  "Commission Expected": "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
};

// Effective status pill maps the lifecycle gate result. Per Phase 2 spec:
//   Active agent  → "Pending" (green) — normal payout flow
//   Pending agent → "Held"    (amber)
//   Suspended    → "Blocked"  (red)
//   Inactive     → "Inactive" (gray)
//   Phase 2.1   → "Paid"     (deep emerald) when admin has marked it paid out
const EFFECTIVE_STATUS_PILL = {
  Pending:  "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  Held:     "bg-amber-500/15 text-amber-300 border-amber-400/40",
  Blocked:  "bg-red-500/15 text-red-300 border-red-400/40",
  Inactive: "bg-white/5 text-white/50 border-white/15",
  Paid:     "bg-emerald-600/30 text-emerald-200 border-emerald-300/50",
  Reversed: "bg-amber-700/20 text-amber-200 border-amber-500/40",
};

// Map the row's effective_status (which the backend computes) to the badge
// label used in the UI. Anything that isn't a lifecycle override (i.e. Active
// agent → row.effective_status === row.status) is displayed as "Pending".
function effectiveLabel(row) {
  const eff = row.effective_status;
  if (eff === "Held" || eff === "Blocked" || eff === "Inactive" || eff === "Paid" || eff === "Reversed") return eff;
  return "Pending";
}

const AVAILABILITY_LABELS = {
  full_time: "Full-time",
  part_time: "Part-time",
  flexible: "Flexible",
};
const TIMELINE_LABELS = {
  immediate: "Immediately",
  "1_2_weeks": "1–2 weeks",
  exploring: "Just exploring",
};

function AdminSuppliersTab() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setSupStatusFilter] = useState("active");
  const [openEditor, setOpenEditor] = useState(null); // null | "new" | supplier object
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const blank = {
    name: "", category: "", logo_url: "", description: "",
    portal_url: "", training_url: "", commission_notes: "",
    preferred: false, status: "active", sort_order: 0,
  };
  const [draft, setDraft] = useState(blank);

  const load = async () => {
    try {
      const { data } = await api.get("/suppliers");
      setData(data);
    } catch {
      setData({ error: true });
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setDraft(blank); setOpenEditor("new"); };
  const startEdit = (s) => { setDraft({ ...blank, ...s }); setOpenEditor(s); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...draft };
      payload.sort_order = Number(payload.sort_order) || 0;
      if (openEditor === "new") {
        await api.post("/admin/suppliers", payload);
        toast.success("Supplier added");
      } else {
        await api.put(`/admin/suppliers/${openEditor.id}`, payload);
        toast.success("Supplier updated");
      }
      setOpenEditor(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save supplier");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/suppliers/${confirmDelete.id}`);
      toast.success("Supplier deleted");
      setConfirmDelete(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  if (data === null) return <div className="py-20 flex justify-center"><CircleNotch size={28} className="text-[#D4AF37] animate-spin" /></div>;
  if (data?.error) return <div className="border border-dashed border-red-400/30 rounded-lg p-12 text-center text-red-300/80 font-sans">Could not load suppliers.</div>;

  const allRows = data.rows || [];
  const filtered = allRows.filter((s) => {
    if (statusFilter !== "all" && (s.status || "active") !== statusFilter) return false;
    if (catFilter !== "all" && s.category !== catFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${s.name} ${s.description || ""} ${CATEGORY_BY_VALUE[s.category]?.label || ""} ${s.portal_url}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-5" data-testid="admin-suppliers-panel">
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          placeholder="Search name, description, URL…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-black/40 border-white/10 text-white flex-1"
          data-testid="admin-suppliers-search"
        />
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="bg-black/40 border-white/10 text-white w-full sm:w-52" data-testid="admin-suppliers-cat-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent className="bg-ink-surface border-white/10 text-white">
            <SelectItem value="all">All categories</SelectItem>
            {SUPPLIER_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setSupStatusFilter}>
          <SelectTrigger className="bg-black/40 border-white/10 text-white w-full sm:w-44" data-testid="admin-suppliers-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-ink-surface border-white/10 text-white">
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={startNew} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-10 px-5 text-xs uppercase tracking-widest sm:shrink-0" data-testid="admin-suppliers-new-btn">
          <Plus size={16} weight="bold" /> Add supplier
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-lg p-16 text-center font-sans" data-testid="admin-suppliers-empty">
          <Storefront size={32} weight="duotone" className="text-white/30 mx-auto mb-3" />
          <p className="text-white/60 text-sm">
            {allRows.length === 0 ? "No suppliers yet — add your first one." : "No suppliers match the current filters."}
          </p>
        </div>
      ) : (
        <div className="border border-white/10 rounded-lg overflow-hidden overflow-x-auto" data-testid="admin-suppliers-table">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-[10px] uppercase tracking-widest text-white/50">
              <tr>
                <th className="text-left px-4 py-3 font-sans">Supplier</th>
                <th className="text-left px-4 py-3 font-sans">Category</th>
                <th className="text-left px-4 py-3 font-sans">Portal</th>
                <th className="text-left px-4 py-3 font-sans">Notes</th>
                <th className="text-left px-4 py-3 font-sans">Status</th>
                <th className="text-right px-4 py-3 font-sans">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const cat = CATEGORY_BY_VALUE[s.category];
                const CatIcon = cat?.Icon || Storefront;
                return (
                  <tr key={s.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-supplier-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                          {s.logo_url ? (
                            <img src={s.logo_url} alt="" className="max-w-full max-h-full object-contain" />
                          ) : (
                            <CatIcon size={18} weight="duotone" className="text-[#D4AF37]" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white truncate flex items-center gap-2">
                            {s.name}
                            {s.preferred && <Star size={11} weight="fill" className="text-[#D4AF37]" />}
                          </p>
                          {s.description && <p className="text-[11px] text-white/40 truncate max-w-xs">{s.description}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border border-white/15 text-white/70 font-sans">
                        <CatIcon size={11} weight="duotone" /> {cat?.label || s.category}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <a href={s.portal_url} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:text-[#F3C853] text-xs flex items-center gap-1 max-w-xs truncate">
                        <ArrowSquareOut size={12} weight="duotone" /> {s.portal_url}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-white/70 text-[11px] max-w-xs truncate" title={s.commission_notes || ""}>
                      {s.commission_notes || <span className="text-white/30">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${(s.status || "active") === "active" ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/40" : "bg-white/5 text-white/40 border-white/10"}`}>
                        {s.status || "active"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(s)} className="text-white/70 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 w-8 p-0" data-testid={`admin-supplier-edit-${s.id}`}>
                          <PencilSimple size={15} weight="duotone" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(s)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0" data-testid={`admin-supplier-delete-${s.id}`}>
                          <Trash size={15} weight="duotone" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / edit dialog */}
      <Dialog open={!!openEditor} onOpenChange={(o) => !o && setOpenEditor(null)}>
        <DialogContent className="bg-ink-surface border-white/10 text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              {openEditor === "new" ? "Add" : "Edit"} <span className="italic text-[#D4AF37]">supplier</span>
            </DialogTitle>
            <DialogDescription className="text-white/50 text-xs font-sans">
              Logo URL: paste a hosted image link (Imgur, Cloudinary, supplier press kit, etc.).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="admin-supplier-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Name *</Label>
                <Input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="sup-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Category *</Label>
                <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                  <SelectTrigger className="bg-black/40 border-white/10" data-testid="sup-category-select"><SelectValue placeholder="Choose category" /></SelectTrigger>
                  <SelectContent className="bg-ink-surface border-white/10 text-white">
                    {SUPPLIER_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Logo URL</Label>
              <Input type="url" value={draft.logo_url} onChange={(e) => setDraft({ ...draft, logo_url: e.target.value })} placeholder="https://..." className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="sup-logo-input" />
              {draft.logo_url && (
                <div className="mt-2 inline-block p-2 rounded bg-black/40 border border-white/10">
                  <img src={draft.logo_url} alt="" className="h-10" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Short description</Label>
              <Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="One-line description shown to agents" className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="sup-description-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Portal URL *</Label>
              <Input required type="url" value={draft.portal_url} onChange={(e) => setDraft({ ...draft, portal_url: e.target.value })} placeholder="https://secure.supplier.com" className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="sup-portal-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Training / resources URL</Label>
              <Input type="url" value={draft.training_url} onChange={(e) => setDraft({ ...draft, training_url: e.target.value })} placeholder="Optional" className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="sup-training-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Commission notes <span className="text-white/30 normal-case tracking-normal">(internal — never shown to agents)</span></Label>
              <Input value={draft.commission_notes} onChange={(e) => setDraft({ ...draft, commission_notes: e.target.value })} placeholder="e.g. 15% on suite bookings" className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="sup-notes-input" />
            </div>
            <div className="flex flex-wrap items-center gap-4 pt-1">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="sup-preferred-toggle">
                <input type="checkbox" checked={!!draft.preferred} onChange={(e) => setDraft({ ...draft, preferred: e.target.checked })} className="h-4 w-4 accent-[#D4AF37] cursor-pointer" />
                <span className="text-sm text-white/80 font-sans">Mark as Preferred</span>
              </label>
              <Select value={draft.status} onValueChange={(v) => setDraft({ ...draft, status: v })}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white w-44 h-9" data-testid="sup-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ink-surface border-white/10 text-white">
                  <SelectItem value="active">Active (visible)</SelectItem>
                  <SelectItem value="archived">Archived (hidden from agents)</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Sort</Label>
                <Input type="number" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} className="w-20 h-9 bg-black/40 border-white/10" data-testid="sup-sort-input" />
              </div>
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpenEditor(null)} className="border-white/20 bg-transparent text-white/80 hover:bg-white/5 h-11 flex-1">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-11 flex-1 text-xs uppercase tracking-widest" data-testid="sup-save-btn">
                {saving ? <CircleNotch size={18} className="animate-spin" /> : openEditor === "new" ? "Create" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-ink-surface border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete supplier?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60 font-sans">
              {confirmDelete && (<><span className="block text-white mb-1">{confirmDelete.name}</span>This removes the supplier from the directory permanently. To temporarily hide instead, archive it.</>)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-supplier-delete-btn">
              Delete supplier
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


// iter39 — Admin: Edit an existing commission row.
// Pre-fills every editable field from the row, live-recalculates advisor
// commission + agency commission as the admin edits gross or split, and
// PATCHes /admin/commissions/{itin_id}/{link_id} on save.
function EditCommissionDialog({ row, agents, open, onOpenChange, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (row) {
      setForm({
        agent_id: row.agent_id || "",
        itinerary_title: row.itinerary_title || "",
        client_name: row.client_name || "",
        category: row.category || "",
        supplier_name: row.supplier_name || "",
        expected_commission: String(row.expected_commission ?? ""),
        commission_currency: row.commission_currency || "USD",
        tier_pct_override: row.tier_pct_override != null ? String(row.tier_pct_override) : String(row.tier_pct ?? 50),
        commission_paid: !!row.commission_paid,
        commission_paid_at: (row.commission_paid_at || "").slice(0, 10),
        notes: row.notes || "",
      });
    } else {
      setForm(null);
    }
  }, [row]);

  if (!form) return null;

  const gross = parseFloat(form.expected_commission) || 0;
  const split = Math.max(0, Math.min(100, parseFloat(form.tier_pct_override) || 0));
  const advisor = Math.round(gross * split) / 100;   // gross * split / 100, 2dp
  const agency = Math.round((gross - advisor) * 100) / 100;
  const currency = (form.commission_currency || "USD").toUpperCase();
  const fmt = (n) => `${currency} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleSave = async () => {
    setBusy(true);
    try {
      const payload = {
        agent_id: form.agent_id || null,
        itinerary_title: form.itinerary_title,
        client_name: form.client_name,
        category: form.category,
        supplier_name: form.supplier_name,
        expected_commission: gross,
        commission_currency: currency,
        tier_pct_override: split,
        commission_paid: form.commission_paid,
        commission_paid_at: form.commission_paid ? (form.commission_paid_at || null) : null,
        notes: form.notes,
      };
      await api.patch(`/admin/commissions/${row.itinerary_id}/${row.supplier_link_id}`, payload);
      toast.success("Commission updated successfully.");
      await onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save commission");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ink-surface border-white/10 text-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-commission-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Edit commission</DialogTitle>
          <DialogDescription className="text-white/60 font-sans text-xs">
            Changes are saved permanently and the row refreshes below.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
          <Field label="Advisor">
            <Select value={form.agent_id} onValueChange={(v) => setForm({ ...form, agent_id: v })}>
              <SelectTrigger className="bg-black/30 border-white/15 text-white h-9" data-testid="edit-commission-agent"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-ink-surface border-white/10 text-white">
                {(agents || []).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Booking / Trip name">
            <Input value={form.itinerary_title} onChange={(e) => setForm({ ...form, itinerary_title: e.target.value })} className="bg-black/30 border-white/15 text-white h-9" data-testid="edit-commission-title" />
          </Field>
          <Field label="Client name">
            <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="bg-black/30 border-white/15 text-white h-9" data-testid="edit-commission-client" />
          </Field>
          <Field label="Booking category">
            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Hotel · Flight · Tour · Cruise" className="bg-black/30 border-white/15 text-white h-9" data-testid="edit-commission-category" />
          </Field>
          <Field label="Supplier" className="sm:col-span-2">
            <Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} className="bg-black/30 border-white/15 text-white h-9" data-testid="edit-commission-supplier" />
          </Field>
          <Field label={`Gross commission (${currency})`}>
            <Input type="number" step="0.01" min="0" value={form.expected_commission} onChange={(e) => setForm({ ...form, expected_commission: e.target.value })} className="bg-black/30 border-white/15 text-white h-9 font-mono" data-testid="edit-commission-gross" />
          </Field>
          <Field label="Split % (advisor share)">
            <Input type="number" step="0.01" min="0" max="100" value={form.tier_pct_override} onChange={(e) => setForm({ ...form, tier_pct_override: e.target.value })} className="bg-black/30 border-white/15 text-white h-9 font-mono" data-testid="edit-commission-split" />
          </Field>
          <Field label="Commission status">
            <Select value={form.commission_paid ? "Paid" : "Pending"} onValueChange={(v) => setForm({ ...form, commission_paid: v === "Paid" })}>
              <SelectTrigger className="bg-black/30 border-white/15 text-white h-9" data-testid="edit-commission-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-ink-surface border-white/10 text-white">
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Payment date">
            <Input type="date" value={form.commission_paid_at} onChange={(e) => setForm({ ...form, commission_paid_at: e.target.value })} disabled={!form.commission_paid} className="bg-black/30 border-white/15 text-white h-9 font-mono disabled:opacity-40" data-testid="edit-commission-paid-at" />
          </Field>
          <Field label="Notes" className="sm:col-span-2">
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full rounded-md bg-black/30 border border-white/15 text-white px-3 py-2 text-sm font-sans" data-testid="edit-commission-notes" />
          </Field>
        </div>

        {/* Live calculation preview */}
        <div className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3 mt-1" data-testid="edit-commission-preview">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2 font-sans">Live calculation</div>
          <div className="grid grid-cols-3 gap-3 text-sm font-sans">
            <div><div className="text-[10px] text-white/50 uppercase tracking-wider">Gross</div><div className="text-white font-mono" data-testid="preview-gross">{fmt(gross)}</div></div>
            <div><div className="text-[10px] text-white/50 uppercase tracking-wider">Advisor {split}%</div><div className="text-emerald-300 font-mono" data-testid="preview-advisor">{fmt(advisor)}</div></div>
            <div><div className="text-[10px] text-white/50 uppercase tracking-wider">Agency {(100 - split).toFixed(2)}%</div><div className="text-white/70 font-mono" data-testid="preview-agency">{fmt(agency)}</div></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} className="text-white/70 hover:text-white" data-testid="edit-commission-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={busy} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black" data-testid="edit-commission-save">
            {busy ? <><CircleNotch size={14} className="animate-spin mr-1.5" />Saving…</> : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-widest text-white/50 font-sans mb-1">{label}</span>
      {children}
    </label>
  );
}


function AdminCommissionsTab() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [agentFilter, setAgentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  // iter23 — Date-based filtering
  const now = new Date();
  const [viewMode, setViewMode] = useState("monthly"); // "monthly" | "yearly" | "all"
  const [monthFilter, setMonthFilter] = useState(now.getMonth() + 1); // 1-12
  const [yearFilter, setYearFilter] = useState(now.getFullYear());
  const [releaseBusy, setReleaseBusy] = useState(null); // agent_id while activating
  const [paidBusy, setPaidBusy] = useState(null); // supplier_link_id while toggling
  const [confirmPaid, setConfirmPaid] = useState(null); // row pending confirmation
  // iter39 — Admin edit dialog
  const [editRow, setEditRow] = useState(null);

  const handleMarkPaid = async (row) => {
    if (!row?.itinerary_id || !row?.supplier_link_id) return;
    const desired = !row.commission_paid;  // toggle
    setPaidBusy(row.supplier_link_id);
    try {
      await api.post(`/admin/commissions/${row.itinerary_id}/${row.supplier_link_id}/mark-paid`, { paid: desired });
      toast.success(desired ? "Marked as paid" : "Payment unmarked");
      const { data } = await api.get(`/agent/commissions`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    } finally {
      setPaidBusy(null);
      setConfirmPaid(null);
    }
  };

  const handleRelease = async (row) => {
    if (!row?.agent_id) return;
    if (!window.confirm(`Activate ${row.agent_name || "this agent"}? Their held commissions will move to the normal payout flow.`)) return;
    setReleaseBusy(row.agent_id);
    try {
      await api.post(`/admin/users/${row.agent_id}/status`, { status: "active", reason: "Released held commissions" });
      toast.success(`${row.agent_name || "Agent"} activated — commissions released`);
      // Reload commissions to reflect new effective_status
      const { data } = await api.get(`/agent/commissions`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not release");
    } finally {
      setReleaseBusy(null);
    }
  };

  const handleExportPayout = () => {
    if (!data?.rows?.length) {
      toast.error("Nothing to export");
      return;
    }
    // iter23 — respect the current Year/Month filter so admins can export just one month if needed.
    const dateGate = (r) => {
      if (viewMode === "all") return true;
      if (!r.commission_date) return false;
      const y = Number(r.commission_date.slice(0, 4));
      if (y !== yearFilter) return false;
      if (viewMode === "monthly") {
        const m = Number(r.commission_date.slice(5, 7));
        if (m !== monthFilter) return false;
      }
      return true;
    };

    // Only rows ready to pay — effective lifecycle gate must be "Pending" (active agent, normal flow).
    const payable = data.rows
      .filter((r) => effectiveLabel(r) === "Pending" && (parseFloat(r.expected_commission) || 0) > 0 && dateGate(r))
      .sort((a, b) => (a.agent_name || "").localeCompare(b.agent_name || "") || (a.itinerary_title || "").localeCompare(b.itinerary_title || ""));

    if (payable.length === 0) {
      toast.error("No payable rows (effective status = Pending) to export");
      return;
    }

    // CSV per spec: Agent, Trip, Client, Supplier, Supplier Commission, Tier %, Agent Payout, Company Share, Currency.
    // Grouped by agent, with a subtotal row after each agent + grand total at the end.
    const esc = (v) => {
      const s = (v == null ? "" : String(v));
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const headers = ["Agent", "Trip", "Client", "Supplier", "Supplier Commission", "Tier %", "Agent Payout", "Company Share", "Currency"];
    const lines = [headers.join(",")];

    let currentAgent = null;
    let agentSubtotals = {};   // { currency: {payout, supplier, company} } for current agent
    const grand = {};          // grand totals per currency, same shape

    const flushAgentSubtotal = () => {
      if (currentAgent && Object.keys(agentSubtotals).length) {
        Object.entries(agentSubtotals).forEach(([cur, t]) => {
          lines.push([
            esc(`${currentAgent} — Subtotal`), "", "", "",
            t.supplier.toFixed(2), "",
            t.payout.toFixed(2),
            t.company.toFixed(2),
            cur,
          ].join(","));
        });
        lines.push("");  // blank separator between agents
      }
    };

    payable.forEach((r) => {
      const agent = r.agent_name || "Unknown agent";
      if (agent !== currentAgent) {
        flushAgentSubtotal();
        currentAgent = agent;
        agentSubtotals = {};
      }
      const supplier = parseFloat(r.expected_commission) || 0;
      const payout = parseFloat(r.agent_payout) || 0;
      const company = parseFloat(r.company_share) || 0;
      const cur = r.commission_currency || "USD";
      const sub = agentSubtotals[cur] || { supplier: 0, payout: 0, company: 0 };
      sub.supplier += supplier; sub.payout += payout; sub.company += company;
      agentSubtotals[cur] = sub;
      const g = grand[cur] || { supplier: 0, payout: 0, company: 0 };
      g.supplier += supplier; g.payout += payout; g.company += company;
      grand[cur] = g;
      lines.push([
        esc(agent),
        esc(r.itinerary_title || ""),
        esc(r.client_name || ""),
        esc(r.supplier_name || ""),
        supplier.toFixed(2),
        `${parseFloat(r.tier_pct) || 50}%`,
        payout.toFixed(2),
        company.toFixed(2),
        cur,
      ].join(","));
    });
    flushAgentSubtotal();

    // Grand total
    Object.entries(grand).forEach(([cur, t]) => {
      lines.push([esc("GRAND TOTAL"), "", "", "", t.supplier.toFixed(2), "", t.payout.toFixed(2), t.company.toFixed(2), cur].join(","));
    });

    const csv = "\uFEFF" + lines.join("\n"); // BOM so Excel reads UTF-8 correctly
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const date = new Date().toISOString().slice(0, 10);
    // iter23 — encode the active date filter into the filename for easy filing
    let suffix = "all-time";
    if (viewMode === "monthly") suffix = `${yearFilter}-${String(monthFilter).padStart(2, "0")}`;
    else if (viewMode === "yearly") suffix = `${yearFilter}`;
    a.href = url;
    a.download = `payout-${suffix}-${date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${payable.length} payable row${payable.length === 1 ? "" : "s"}`);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/agent/commissions`);
        setData(data);
      } catch {
        setData({ error: true });
      }
    })();
  }, []);

  if (data === null) return <div className="py-20 flex justify-center"><CircleNotch size={28} className="text-[#D4AF37] animate-spin" /></div>;
  if (data?.error) return <div className="border border-dashed border-red-400/30 rounded-lg p-12 text-center text-red-300/80 font-sans">Could not load commissions.</div>;

  const allRows = data.rows || [];
  const agents = Array.from(new Map(allRows.filter(r => r.agent_id).map(r => [r.agent_id, r.agent_name || ""])).entries())
    .map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));

  // iter23 — Year options drawn from data + current year
  const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const yearsInData = Array.from(new Set(
    allRows.map((r) => r.commission_date ? Number(r.commission_date.slice(0, 4)) : null).filter(Boolean)
  )).sort((a, b) => b - a);
  if (!yearsInData.includes(now.getFullYear())) yearsInData.unshift(now.getFullYear());

  // Dynamic "Showing:" label
  let showingLabel;
  if (viewMode === "monthly") showingLabel = `${MONTHS[monthFilter - 1]} ${yearFilter}`;
  else if (viewMode === "yearly") showingLabel = `${yearFilter}`;
  else showingLabel = "All time";

  const filtered = allRows.filter((r) => {
    if (agentFilter !== "all" && r.agent_id !== agentFilter) return false;
    if (statusFilter !== "all") {
      if (effectiveLabel(r) !== statusFilter) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      const hay = `${r.supplier_name} ${r.itinerary_title} ${r.client_name} ${r.category} ${r.agent_name || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    // Date gate — rows without a commission_date are only shown in "All time"
    if (viewMode !== "all") {
      if (!r.commission_date) return false;
      const y = Number(r.commission_date.slice(0, 4));
      const m = Number(r.commission_date.slice(5, 7));
      if (y !== yearFilter) return false;
      if (viewMode === "monthly" && m !== monthFilter) return false;
    }
    return true;
  });

  // Totals for the filtered set, broken out by effective gate
  const byCurrency = {};
  const byEffective = { Pending: 0, Held: 0, Blocked: 0, Inactive: 0, Paid: 0, Reversed: 0 };
  // iter23 — dollar sums for "Paid Out" + "Pending" cards (primary currency only = USD)
  let paidUSD = 0;
  let pendingUSD = 0;
  filtered.forEach((r) => {
    const cur = r.commission_currency || "USD";
    byCurrency[cur] = (byCurrency[cur] || 0) + (parseFloat(r.expected_commission) || 0);
    const eff = effectiveLabel(r);
    byEffective[eff] = (byEffective[eff] || 0) + 1;
    if (cur === "USD") {
      const amt = parseFloat(r.expected_commission) || 0;
      if (eff === "Paid") paidUSD += amt;
      else if (eff === "Pending" || eff === "Held") pendingUSD += amt;
    }
  });

  // iter23 — 12-month bar chart (respects selected Year; counts USD only for now)
  const monthlyByYear = Array.from({ length: 12 }, () => 0);
  allRows.forEach((r) => {
    if (!r.commission_date) return;
    const y = Number(r.commission_date.slice(0, 4));
    if (y !== yearFilter) return;
    if (agentFilter !== "all" && r.agent_id !== agentFilter) return;
    if ((r.commission_currency || "USD") !== "USD") return;
    const m = Number(r.commission_date.slice(5, 7));
    monthlyByYear[m - 1] += parseFloat(r.expected_commission) || 0;
  });
  const chartMax = Math.max(...monthlyByYear, 1);

  return (
    <div className="space-y-6" data-testid="admin-commissions-panel">
      {/* iter23 — Date filter toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 border border-[#D4AF37]/20 bg-black/30 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans">View</span>
          <div className="inline-flex rounded-md border border-white/10 overflow-hidden" role="tablist">
            {[
              { v: "monthly", label: "Monthly" },
              { v: "yearly", label: "Yearly" },
              { v: "all", label: "All time" },
            ].map((m) => (
              <button
                key={m.v}
                type="button"
                onClick={() => setViewMode(m.v)}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-sans transition ${viewMode === m.v ? "bg-[#D4AF37] text-black" : "text-white/70 hover:bg-white/5"}`}
                data-testid={`commission-view-${m.v}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {viewMode !== "all" && (
          <div className="flex items-center gap-2">
            <Select value={String(yearFilter)} onValueChange={(v) => setYearFilter(Number(v))}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white w-28 h-9" data-testid="commission-year-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-ink-surface border-white/10 text-white">
                {yearsInData.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            {viewMode === "monthly" && (
              <Select value={String(monthFilter)} onValueChange={(v) => setMonthFilter(Number(v))}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white w-36 h-9" data-testid="commission-month-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ink-surface border-white/10 text-white">
                  {MONTHS.map((name, idx) => <SelectItem key={name} value={String(idx + 1)}>{name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        <div className="sm:ml-auto text-xs text-white/70 font-sans">
          Showing: <span className="text-[#D4AF37] font-semibold" data-testid="commission-showing-label">{showingLabel}</span>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/10 to-transparent rounded-lg p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans">Rows</p>
          <p className="font-serif text-3xl text-white mt-1" data-testid="commission-row-count">{filtered.length}</p>
          <p className="text-xs text-white/50 font-sans mt-1">Across {agents.length || 1} agent{agents.length === 1 ? "" : "s"}</p>
        </div>
        <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/10 to-transparent rounded-lg p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans">Expected commission</p>
          <div className="mt-1 space-y-0.5">
            {Object.keys(byCurrency).length === 0 && <p className="font-serif text-2xl text-white/40">—</p>}
            {Object.entries(byCurrency).sort((a, b) => b[1] - a[1]).map(([cur, amt]) => (
              <p key={cur} className="font-serif text-2xl text-white" data-testid={`commission-total-${cur}`}>
                {cur} {amt.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            ))}
          </div>
        </div>
        <div className="border border-emerald-400/40 bg-gradient-to-br from-emerald-500/10 to-transparent rounded-lg p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-sans">Paid out (USD)</p>
          <p className="font-serif text-2xl text-white mt-1" data-testid="commission-paid-usd">
            ${paidUSD.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-white/50 font-sans mt-1">This {viewMode === "yearly" ? "year" : viewMode === "monthly" ? "month" : "period"}</p>
        </div>
        <div className="border border-amber-400/40 bg-gradient-to-br from-amber-500/10 to-transparent rounded-lg p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-sans">Pending payout (USD)</p>
          <p className="font-serif text-2xl text-white mt-1" data-testid="commission-pending-usd">
            ${pendingUSD.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-white/50 font-sans mt-1">Owed to agents</p>
        </div>
        <div className="border border-white/10 bg-ink-surface rounded-lg p-5 col-span-2 lg:col-span-1">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">By payout status</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {["Pending", "Paid", "Held", "Blocked", "Inactive", "Reversed"].map((s) => (
              <span key={s} className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${EFFECTIVE_STATUS_PILL[s]}`} data-testid={`commission-summary-${s.toLowerCase()}`}>
                {s} · {byEffective[s] || 0}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* iter23 — 12-month bar chart (USD only, respects Year + Agent filter) */}
      <div className="border border-white/10 bg-ink-surface rounded-lg p-5" data-testid="commission-monthly-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans">Monthly commissions</p>
            <p className="font-serif text-lg text-white mt-0.5">{yearFilter} · USD</p>
          </div>
          <p className="text-xs text-white/40 font-sans">Click a bar to jump to that month</p>
        </div>
        <div className="flex items-end gap-2 h-40">
          {monthlyByYear.map((v, i) => {
            const pct = (v / chartMax) * 100;
            const isSel = viewMode === "monthly" && i + 1 === monthFilter;
            return (
              <button
                key={i}
                type="button"
                onClick={() => { setMonthFilter(i + 1); setViewMode("monthly"); }}
                className={`flex-1 flex flex-col items-center justify-end group rounded-t transition ${isSel ? "bg-[#D4AF37]" : "bg-[#D4AF37]/30 hover:bg-[#D4AF37]/60"}`}
                style={{ height: `${Math.max(pct, v > 0 ? 4 : 2)}%`, minHeight: v > 0 ? "6px" : "2px" }}
                title={`${MONTHS[i]} ${yearFilter} — $${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                data-testid={`commission-chart-bar-${i + 1}`}
              />
            );
          })}
        </div>
        <div className="mt-2 flex gap-2">
          {MONTHS.map((name, i) => (
            <div key={name} className={`flex-1 text-center text-[9px] uppercase tracking-widest font-sans ${viewMode === "monthly" && i + 1 === monthFilter ? "text-[#D4AF37]" : "text-white/40"}`}>
              {name.slice(0, 3)}
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          placeholder="Search supplier, trip, client, agent…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-black/40 border-white/10 text-white flex-1"
          data-testid="commission-search"
        />
        <Select value={agentFilter} onValueChange={setAgentFilter}>
          <SelectTrigger className="bg-black/40 border-white/10 text-white w-full sm:w-56" data-testid="commission-agent-filter"><SelectValue placeholder="All agents" /></SelectTrigger>
          <SelectContent className="bg-ink-surface border-white/10 text-white">
            <SelectItem value="all">All agents</SelectItem>
            {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-black/40 border-white/10 text-white w-full sm:w-52" data-testid="commission-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent className="bg-ink-surface border-white/10 text-white">
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Pending">Pending (normal flow)</SelectItem>
            <SelectItem value="Paid">Paid (paid out)</SelectItem>
            <SelectItem value="Held">Held (pending agents)</SelectItem>
            <SelectItem value="Blocked">Blocked (suspended)</SelectItem>
            <SelectItem value="Inactive">Inactive</SelectItem>
            <SelectItem value="Reversed">Reversed (admin undone)</SelectItem>
          </SelectContent>
        </Select>
        <Button
          type="button"
          onClick={handleExportPayout}
          className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-10 px-4 text-xs uppercase tracking-widest sm:shrink-0"
          data-testid="commission-export-btn"
        >
          <DownloadSimple size={15} weight="duotone" /> Export Payout (CSV)
        </Button>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-lg p-16 text-center text-white/50 font-sans" data-testid="commission-empty">
          {allRows.length === 0 ? "No supplier commissions tracked yet." : "No rows match the current filters."}
        </div>
      ) : (
        <div className="border border-white/10 rounded-lg overflow-hidden overflow-x-auto" data-testid="admin-commissions-table">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-[10px] uppercase tracking-widest text-white/50">
              <tr>
                <th className="text-left px-4 py-3 font-sans">Advisor</th>
                <th className="text-left px-4 py-3 font-sans">Booking</th>
                <th className="text-left px-4 py-3 font-sans">Client</th>
                <th className="text-left px-4 py-3 font-sans">Category</th>
                <th className="text-left px-4 py-3 font-sans">Supplier</th>
                <th className="text-left px-4 py-3 font-sans">Link</th>
                <th className="text-right px-4 py-3 font-sans">Gross Commission</th>
                <th className="text-right px-4 py-3 font-sans">Split</th>
                <th className="text-right px-4 py-3 font-sans">Advisor Commission</th>
                <th className="text-right px-4 py-3 font-sans">Agency Commission</th>
                <th className="text-left px-4 py-3 font-sans">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={`${r.itinerary_id}-${i}`} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`commission-row-${i}`}>
                  <td className="px-4 py-3">
                    <p className="text-white">{r.agent_name || "—"}</p>
                    {r.is_manual && (
                      <span className="inline-flex mt-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] font-sans" data-testid={`commission-manual-badge-${i}`}>
                        Manual Entry
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/itineraries/${r.itinerary_id}`} className="text-[#D4AF37] hover:text-[#F3C853]">{r.itinerary_title}</Link>
                    <p className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">{r.trip_status || ""}</p>
                  </td>
                  <td className="px-4 py-3 text-white/80">{r.client_name || "—"}</td>
                  <td className="px-4 py-3 text-white/80">{r.category || "Other"}</td>
                  <td className="px-4 py-3">
                    <p className="text-white">{r.supplier_name || "—"}</p>
                    <p className="text-[10px] text-white/50 mt-0.5">
                      {r.visible_to_client ? <span className="inline-flex items-center gap-1"><Eye size={10} weight="duotone" className="text-emerald-300" />Client-visible</span> : <span className="inline-flex items-center gap-1"><EyeSlash size={10} weight="duotone" />Internal</span>}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    {r.payment_link ? (
                      <a href={r.payment_link} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:text-[#F3C853] text-xs flex items-center gap-1">
                        <ArrowSquareOut size={12} weight="duotone" /> Open
                      </a>
                    ) : <span className="text-white/30 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-white/90" data-testid={`commission-supplier-${i}`}>
                    {r.commission_currency || "USD"} {(parseFloat(r.expected_commission) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-right" data-testid={`commission-tier-${i}`}>
                    <TierBadge tier={{ name: r.tier_name || "Standard", rate: r.tier_pct || 50 }} size="sm" />
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-300" data-testid={`commission-agent-payout-${i}`}>
                    {r.commission_currency || "USD"} {(parseFloat(r.agent_payout) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-white/60" data-testid={`commission-company-share-${i}`}>
                    {r.commission_currency || "USD"} {(parseFloat(r.company_share) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex w-fit px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${EFFECTIVE_STATUS_PILL[effectiveLabel(r)]}`} data-testid={`commission-effective-${i}`}>
                        {effectiveLabel(r)}
                      </span>
                      <span className="text-[10px] text-white/40 font-sans">
                        {r.status || "Link Added"}
                      </span>
                      {effectiveLabel(r) === "Held" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRelease(r)}
                          disabled={releaseBusy === r.agent_id}
                          className="h-6 mt-1 px-2 border-emerald-400/40 bg-transparent text-emerald-300 hover:bg-emerald-500/10 text-[10px] uppercase tracking-widest"
                          data-testid={`commission-release-${i}`}
                        >
                          {releaseBusy === r.agent_id ? <CircleNotch size={11} className="animate-spin" /> : <><Play size={10} weight="duotone" className="mr-1" /> Release → Pending</>}
                        </Button>
                      )}
                      {effectiveLabel(r) === "Pending" && (parseFloat(r.expected_commission) || 0) > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmPaid(r)}
                          disabled={paidBusy === r.supplier_link_id}
                          className="h-6 mt-1 px-2 border-emerald-400/40 bg-transparent text-emerald-300 hover:bg-emerald-500/10 text-[10px] uppercase tracking-widest"
                          data-testid={`commission-mark-paid-${i}`}
                        >
                          {paidBusy === r.supplier_link_id ? <CircleNotch size={11} className="animate-spin" /> : <><CheckCircle size={10} weight="duotone" className="mr-1" /> Mark as Paid</>}
                        </Button>
                      )}
                      {effectiveLabel(r) === "Paid" && (
                        <>
                          {r.commission_paid_at && (
                            <span className="text-[9px] uppercase tracking-widest text-emerald-300/70 font-sans">
                              Paid {new Date(r.commission_paid_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                            </span>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleMarkPaid(r)}
                            disabled={paidBusy === r.supplier_link_id}
                            className="h-5 mt-0.5 px-1 text-white/40 hover:text-white/70 text-[9px] uppercase tracking-widest"
                            data-testid={`commission-unmark-paid-${i}`}
                          >
                            Undo
                          </Button>
                        </>
                      )}
                      {/* iter39 — Admin Edit action, inline beside Mark as Paid / Undo */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditRow(r)}
                        className="h-6 mt-1 px-2 border-[#D4AF37]/40 bg-transparent text-[#D4AF37] hover:bg-[#D4AF37]/10 text-[10px] uppercase tracking-widest w-fit"
                        title="Edit commission"
                        data-testid={`commission-edit-${i}`}
                      >
                        <PencilSimple size={10} weight="duotone" className="mr-1" /> Edit
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-white/40 italic">
        All commission data is internal — never shown to clients. Mixed currencies are summed by currency above; convert before reporting if needed.
      </p>

      {/* iter39 — Admin Edit Commission dialog */}
      <EditCommissionDialog
        row={editRow}
        agents={(data?.rows || []).reduce((acc, r) => {
          if (r.agent_id && !acc.some(a => a.id === r.agent_id)) {
            acc.push({ id: r.agent_id, name: r.agent_name });
          }
          return acc;
        }, [])}
        open={!!editRow}
        onOpenChange={(o) => !o && setEditRow(null)}
        onSaved={async () => {
          setEditRow(null);
          const { data } = await api.get(`/agent/commissions`);
          setData(data);
        }}
      />

      <AlertDialog open={!!confirmPaid} onOpenChange={(o) => !o && setConfirmPaid(null)}>
        <AlertDialogContent className="bg-ink-surface border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">
              Mark commission as <span className="italic text-emerald-300">paid</span>?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60 font-sans">
              {confirmPaid && (
                <>
                  <span className="block text-white mb-1">
                    {(confirmPaid.commission_currency || "USD")}{" "}
                    {(parseFloat(confirmPaid.expected_commission) || 0).toFixed(2)}
                    {" — "}
                    {confirmPaid.supplier_name || "Supplier"} · {confirmPaid.itinerary_title}
                  </span>
                  This row will be excluded from future payout exports and pending payout totals. You can undo from the Status column if needed.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmPaid && handleMarkPaid(confirmPaid)}
              className="bg-emerald-500 hover:bg-emerald-400 text-black"
              data-testid="confirm-mark-paid-commission-btn"
            >
              Mark as paid
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


const REFERRAL_STATUS_PILL = {
  pending: "bg-amber-500/10 text-amber-300 border-amber-400/30",
  qualified: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
};
const REFERRAL_BONUS_PILL = {
  earned: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  paid: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  pending: "bg-white/5 text-white/50 border-white/10",
};

function AdminReferralsTab() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null); // referralId-bonusKind while marking paid
  const [confirmTarget, setConfirmTarget] = useState(null); // { referralId, bonusKind, referredName, amount }

  const load = async () => {
    try {
      const { data } = await api.get("/admin/referrals");
      setData(data);
    } catch {
      setData({ error: true });
    }
  };

  useEffect(() => { load(); }, []);

  const doMarkPaid = async () => {
    if (!confirmTarget) return;
    const { referralId, bonusKind } = confirmTarget;
    setBusyId(`${referralId}-${bonusKind}`);
    try {
      await api.post(`/admin/referrals/${referralId}/mark-paid`, { bonus_kind: bonusKind });
      toast.success(`${bonusKind === "signup" ? "Signup" : "Booking"} bonus marked paid`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark paid");
    } finally {
      setBusyId(null);
      setConfirmTarget(null);
    }
  };

  if (data === null) return <div className="py-20 flex justify-center"><CircleNotch size={28} className="text-[#D4AF37] animate-spin" /></div>;
  if (data?.error) return <div className="border border-dashed border-red-400/30 rounded-lg p-12 text-center text-red-300/80 font-sans">Could not load referrals.</div>;

  const summary = data.summary || {};
  const rows = data.rows || [];

  return (
    <div className="space-y-6" data-testid="admin-referrals-panel">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/10 to-transparent rounded-lg p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans">Total referrals</p>
          <p className="font-serif text-3xl text-white mt-1" data-testid="admin-ref-total">{summary.total_referrals || 0}</p>
          <p className="text-xs text-white/50 font-sans mt-1">{summary.qualified_referrals || 0} qualified · {summary.pending_referrals || 0} pending</p>
        </div>
        <div className="border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-transparent rounded-lg p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-sans">Pending payout</p>
          <p className="font-serif text-3xl text-white mt-1" data-testid="admin-ref-pending">${(summary.pending_total || 0).toLocaleString()}</p>
          <p className="text-xs text-white/50 font-sans mt-1">Earned, not yet paid</p>
        </div>
        <div className="border border-emerald-400/40 bg-gradient-to-br from-emerald-500/10 to-transparent rounded-lg p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-sans">Paid out</p>
          <p className="font-serif text-3xl text-white mt-1" data-testid="admin-ref-paid">${(summary.paid_total || 0).toLocaleString()}</p>
          <p className="text-xs text-white/50 font-sans mt-1">Lifetime payouts</p>
        </div>
        <div className="border border-white/10 bg-ink-surface rounded-lg p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Lifetime earned</p>
          <p className="font-serif text-3xl text-white mt-1" data-testid="admin-ref-lifetime">${(summary.earned_total || 0).toLocaleString()}</p>
          <p className="text-xs text-white/50 font-sans mt-1">All bonuses earned</p>
        </div>
      </div>

      {/* Table */}
      {rows.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-lg p-16 text-center font-sans" data-testid="admin-ref-empty">
          <Gift size={32} weight="duotone" className="text-white/30 mx-auto mb-3" />
          <p className="text-white/60 text-sm">No referrals on the platform yet.</p>
        </div>
      ) : (
        <div className="border border-white/10 rounded-lg overflow-hidden overflow-x-auto" data-testid="admin-referrals-table">
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-[10px] uppercase tracking-widest text-white/50">
              <tr>
                <th className="text-left px-4 py-3 font-sans">Referring agent</th>
                <th className="text-left px-4 py-3 font-sans">Referred agent</th>
                <th className="text-left px-4 py-3 font-sans">Joined</th>
                <th className="text-left px-4 py-3 font-sans">Status</th>
                <th className="text-left px-4 py-3 font-sans">Signup bonus</th>
                <th className="text-left px-4 py-3 font-sans">Booking bonus</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-ref-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <p className="text-white">{r.referring_agent_name || "—"}</p>
                    <p className="text-[11px] text-white/40 font-mono">{r.referring_agent_email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-white">{r.referred_agent_name || r.referred_email?.split("@")[0]}</p>
                    <p className="text-[11px] text-white/40 font-mono">{r.referred_email}</p>
                    {r.referred_agent_active === false && (
                      <p className="text-[10px] text-red-300/80 mt-0.5">Inactive</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-white/70 whitespace-nowrap">
                    {r.date_joined ? new Date(r.date_joined).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${REFERRAL_STATUS_PILL[r.status] || REFERRAL_STATUS_PILL.pending}`}>
                      {r.status === "qualified" ? "Qualified" : "Pending booking"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <BonusCell
                      amount={r.signup_bonus_amount}
                      status={r.signup_bonus_status}
                      onMarkPaid={() => setConfirmTarget({ referralId: r.id, bonusKind: "signup", referredName: r.referred_agent_name || r.referred_email, amount: r.signup_bonus_amount })}
                      busy={busyId === `${r.id}-signup`}
                      testIdPrefix={`signup-${r.id}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <BonusCell
                      amount={r.booking_bonus_amount}
                      status={r.booking_bonus_status}
                      onMarkPaid={() => setConfirmTarget({ referralId: r.id, bonusKind: "booking", referredName: r.referred_agent_name || r.referred_email, amount: r.booking_bonus_amount })}
                      busy={busyId === `${r.id}-booking`}
                      testIdPrefix={`booking-${r.id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-white/40 italic">
        Mark each bonus as paid only after the actual payout has been processed. The referring agent receives an email confirmation.
      </p>

      <AlertDialog open={!!confirmTarget} onOpenChange={(o) => !o && setConfirmTarget(null)}>
        <AlertDialogContent className="bg-ink-surface border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">
              Mark <span className="italic text-[#D4AF37]">{confirmTarget?.bonusKind}</span> bonus as paid?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60 font-sans">
              {confirmTarget && (
                <>
                  <span className="block text-white mb-1">
                    ${(confirmTarget.amount || 0).toFixed(0)} {confirmTarget.bonusKind} bonus for <strong>{confirmTarget.referredName}</strong>
                  </span>
                  This action notifies the referring agent by email that the payout has been processed. It cannot be undone from the dashboard.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5" data-testid="confirm-mark-paid-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doMarkPaid}
              className="bg-[#D4AF37] hover:bg-[#F3C853] text-black"
              data-testid="confirm-mark-paid-btn"
            >
              Mark as paid
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BonusCell({ amount, status, onMarkPaid, busy, testIdPrefix }) {
  const cls = REFERRAL_BONUS_PILL[status] || REFERRAL_BONUS_PILL.pending;
  const label = status === "paid" ? "Paid" : status === "earned" ? "Earned" : "Pending";
  return (
    <div className="flex items-center gap-2">
      <span className="text-white/90 font-mono">${(amount || 0).toFixed(0)}</span>
      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${cls}`} data-testid={`${testIdPrefix}-status`}>
        {label}
      </span>
      {status === "earned" && (
        <Button
          size="sm"
          variant="outline"
          onClick={onMarkPaid}
          disabled={busy}
          className="h-7 px-2 border-[#D4AF37]/40 bg-transparent text-[#D4AF37] hover:bg-[#D4AF37]/10 text-[10px] uppercase tracking-widest"
          data-testid={`${testIdPrefix}-mark-paid-btn`}
        >
          {busy ? <CircleNotch size={12} className="animate-spin" /> : "Mark paid"}
        </Button>
      )}
    </div>
  );
}


function AdminSalesTab() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  // iter24 — manual sales state
  const [manual, setManual] = useState(null);
  const [manualAgents, setManualAgents] = useState([]);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadManual = async () => {
    try {
      const qs = new URLSearchParams({ year });
      if (month) qs.set("month", month);
      const { data } = await api.get(`/admin/manual-sales?${qs.toString()}`);
      setManual(data);
    } catch {
      setManual({ error: true, rows: [], total: 0 });
    }
  };

  useEffect(() => {
    (async () => {
      setData(null);
      try {
        const qs = new URLSearchParams({ year });
        if (month) qs.set("month", month);
        const { data } = await api.get(`/admin/sales?${qs.toString()}`);
        setData(data);
      } catch {
        setData({ error: true });
      }
    })();
    loadManual();
  }, [year, month]);

  useEffect(() => {
    // Load agent dropdown once
    (async () => {
      try {
        const { data } = await api.get(`/admin/users`);
        setManualAgents((data || []).filter((u) => u.role === "agent").map((u) => ({ id: u.id, name: u.name || u.email.split("@")[0], email: u.email })));
      } catch {
        setManualAgents([]);
      }
    })();
  }, []);

  const handleDeleteManual = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/manual-sales/${deleteTarget.id}`);
      toast.success(`Manual sale deleted`);
      setDeleteTarget(null);
      loadManual();
      // Reload sales aggregates too since revenue changes
      const qs = new URLSearchParams({ year });
      if (month) qs.set("month", month);
      const { data } = await api.get(`/admin/sales?${qs.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  if (!data) {
    return <div className="py-20 flex justify-center"><CircleNotch size={28} className="text-[#D4AF37] animate-spin" /></div>;
  }
  if (data.error) {
    return <div className="rounded-lg border border-red-400/30 bg-red-500/5 p-6 text-red-300 text-sm font-sans">Failed to load sales data.</div>;
  }

  const t = data.totals;
  const rows = data.agents || [];
  const years = [0, 1, 2].map((o) => String(new Date().getFullYear() - o));

  return (
    <div className="space-y-8" data-testid="admin-sales-panel">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1.5 block font-sans">Year</Label>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="h-10 w-[140px] bg-ink-surface border-white/10 focus:border-[#D4AF37]" data-testid="admin-sales-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-ink-surface border-white/10 text-white">
              {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1.5 block font-sans">Month</Label>
          <Select value={month || "all"} onValueChange={(v) => setMonth(v === "all" ? "" : v)}>
            <SelectTrigger className="h-10 w-[180px] bg-ink-surface border-white/10 focus:border-[#D4AF37]" data-testid="admin-sales-month">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-ink-surface border-white/10 text-white">
              {MONTH_OPTIONS.map((m) => <SelectItem key={m.value || "all"} value={m.value || "all"}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Link
          to="/leaderboard"
          data-testid="admin-sales-leaderboard-link"
          className="ml-auto inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-[#D4AF37] transition-colors font-sans"
        >
          <Trophy size={16} weight="duotone" /> Public leaderboard
        </Link>
        <Button
          type="button"
          onClick={() => { setEditingSale(null); setManualDialogOpen(true); }}
          className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-10 px-4 text-xs uppercase tracking-widest"
          data-testid="admin-add-manual-sale-btn"
        >
          <Plus size={15} weight="bold" /> Add Manual Sale
        </Button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-sales-totals">
        <div className="rounded-lg border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2 font-sans">Annual revenue</p>
          <p className="font-serif text-3xl text-[#D4AF37]">{formatMoney(t.annual_revenue)}</p>
          <p className="text-[11px] text-white/40 font-sans mt-1">{t.annual_bookings} bookings · {data.year}</p>
        </div>
        <div className="rounded-lg border border-white/10 bg-ink-surface p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2 font-sans">{month ? MONTH_OPTIONS.find((m) => m.value === month)?.label : "This month"}</p>
          <p className="font-serif text-3xl text-white">{formatMoney(t.monthly_revenue)}</p>
          <p className="text-[11px] text-white/40 font-sans mt-1">{t.monthly_bookings} bookings</p>
        </div>
        <div className="rounded-lg border border-white/10 bg-ink-surface p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2 font-sans">Lifetime revenue</p>
          <p className="font-serif text-3xl text-white">{formatMoney(t.total_revenue)}</p>
          <p className="text-[11px] text-white/40 font-sans mt-1">{t.total_bookings} bookings</p>
        </div>
        <div className="rounded-lg border border-white/10 bg-ink-surface p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2 font-sans">Agents</p>
          <p className="font-serif text-3xl text-white">{t.active_agent_count}<span className="text-white/30 text-xl">/{t.agent_count}</span></p>
          <p className="text-[11px] text-white/40 font-sans mt-1">active / total</p>
        </div>
      </div>

      {/* Per-agent table */}
      <div className="border border-white/10 rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-ink-surface text-[10px] uppercase tracking-widest text-white/50 border-b border-white/10 font-sans">
          <div className="col-span-1">Rank</div>
          <div className="col-span-3">Agent</div>
          <div className="col-span-2 text-right">Annual</div>
          <div className="col-span-2 text-right">Selected period</div>
          <div className="col-span-1 text-right">Bookings</div>
          <div className="col-span-3 text-right">Tier</div>
        </div>
        {rows.length === 0 ? (
          <div className="p-10 text-center text-white/50 text-sm font-sans">No agents yet.</div>
        ) : (
          rows.map((r) => (
            <div key={r.agent_id} className="grid grid-cols-12 gap-4 px-5 py-4 text-sm items-center border-b border-white/5 hover:bg-ink-surface transition-colors" data-testid={`admin-sales-row-${r.rank}`}>
              <div className="col-span-1 font-sans text-white/60">#{r.rank}</div>
              <div className="col-span-3 min-w-0">
                <p className="text-white font-sans truncate">{r.name}</p>
                <p className="text-white/40 text-xs font-sans truncate">{r.email}</p>
              </div>
              <div className="col-span-2 text-right font-serif text-[#D4AF37]">{formatMoney(r.annual_revenue)}</div>
              <div className="col-span-2 text-right font-serif text-white/90">{formatMoney(r.monthly_revenue)}</div>
              <div className="col-span-1 text-right font-sans text-white/80">{r.annual_bookings}</div>
              <div className="col-span-3 text-right"><TierBadge tier={r.tier} size="sm" /></div>
            </div>
          ))
        )}
      </div>

      <p className="text-[11px] text-white/30 font-sans italic">
        Sales are counted only for itineraries that are <strong className="text-white/60">Approved AND Booked</strong>. Currency totals are aggregated as USD-equivalent. <strong className="text-white/60">Manual sales</strong> added by admin are included in all totals.
      </p>

      {/* iter24 — Manual Sales Log */}
      <div className="space-y-3" data-testid="admin-manual-sales-log">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-serif text-xl text-white">Manual Sales Log</h3>
            <p className="text-[11px] text-white/40 font-sans mt-0.5">
              {manual?.total || 0} manual entries for {month ? MONTH_OPTIONS.find((m) => m.value === month)?.label + " " : ""}{year} · ${(manual?.total_amount || 0).toLocaleString()} booking value · ${(manual?.total_commission || 0).toLocaleString()} commission
            </p>
          </div>
        </div>
        <div className="border border-white/10 rounded-lg overflow-hidden overflow-x-auto">
          {manual === null ? (
            <div className="p-8 flex justify-center"><CircleNotch size={20} className="text-[#D4AF37] animate-spin" /></div>
          ) : (manual.rows || []).length === 0 ? (
            <div className="p-8 text-center text-white/40 text-sm font-sans">No manual sales for this period. Click <span className="text-[#D4AF37]">+ Add Manual Sale</span> above to capture missed bookings.</div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-manual-sales-table">
              <thead className="bg-black/40 text-[10px] uppercase tracking-widest text-white/50">
                <tr>
                  <th className="text-left px-4 py-3 font-sans">Booking date</th>
                  <th className="text-left px-4 py-3 font-sans">Agent</th>
                  <th className="text-left px-4 py-3 font-sans">Trip</th>
                  <th className="text-left px-4 py-3 font-sans">Client</th>
                  <th className="text-left px-4 py-3 font-sans">Supplier</th>
                  <th className="text-right px-4 py-3 font-sans">Amount</th>
                  <th className="text-right px-4 py-3 font-sans">Commission</th>
                  <th className="text-left px-4 py-3 font-sans">Status</th>
                  <th className="text-right px-4 py-3 font-sans">Actions</th>
                </tr>
              </thead>
              <tbody>
                {manual.rows.map((r, i) => (
                  <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`manual-sale-row-${i}`}>
                    <td className="px-4 py-3 text-white/80 whitespace-nowrap">{r.booking_date || "—"}</td>
                    <td className="px-4 py-3">
                      <p className="text-white">{r.agent_name}</p>
                      <span className="inline-flex mt-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] font-sans" data-testid={`manual-entry-badge-${i}`}>
                        Manual Entry
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white">{r.trip_name}</td>
                    <td className="px-4 py-3 text-white/80">{r.client_name}</td>
                    <td className="px-4 py-3 text-white/80">
                      {r.supplier_name}
                      <p className="text-[10px] text-white/40 mt-0.5">{r.category}</p>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-white/90">${r.booking_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#D4AF37]">${r.supplier_commission.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${EFFECTIVE_STATUS_PILL[r.status] || EFFECTIVE_STATUS_PILL.Pending}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => { setEditingSale(r); setManualDialogOpen(true); }}
                          className="h-7 px-2 border-white/15 bg-transparent text-white/70 hover:bg-white/5 text-[10px] uppercase tracking-widest"
                          data-testid={`manual-sale-edit-${i}`}
                        >
                          <PencilSimple size={11} weight="duotone" />
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setDeleteTarget(r)}
                          className="h-7 px-2 border-red-400/30 bg-transparent text-red-300 hover:bg-red-500/10 text-[10px] uppercase tracking-widest"
                          data-testid={`manual-sale-delete-${i}`}
                        >
                          <Trash size={11} weight="duotone" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ManualSaleDialog
        open={manualDialogOpen}
        onOpenChange={setManualDialogOpen}
        editing={editingSale}
        agents={manualAgents}
        onSaved={async () => {
          setManualDialogOpen(false);
          setEditingSale(null);
          await loadManual();
          // refresh aggregates
          const qs = new URLSearchParams({ year });
          if (month) qs.set("month", month);
          const { data } = await api.get(`/admin/sales?${qs.toString()}`);
          setData(data);
        }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-ink-surface border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete manual sale?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60 font-sans">
              {deleteTarget && (
                <>
                  <span className="block text-white mb-1">{deleteTarget.trip_name} — {deleteTarget.client_name}</span>
                  This will remove ${deleteTarget.booking_amount?.toLocaleString()} from the agent's totals and commissions. Cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteManual}
              className="bg-red-500 hover:bg-red-400 text-white"
              data-testid="confirm-delete-manual-sale-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// iter24 — Manual Sale create/edit dialog
function ManualSaleDialog({ open, onOpenChange, editing, agents, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const today = new Date().toISOString().slice(0, 10);
    if (editing) {
      setForm({
        agent_id: editing.agent_id || "",
        client_name: editing.client_name || "",
        trip_name: editing.trip_name || "",
        supplier_name: editing.supplier_name || "",
        category: editing.category || "Other",
        booking_amount: String(editing.booking_amount ?? ""),
        supplier_commission: String(editing.supplier_commission ?? ""),
        commission_date: editing.commission_date || today,
        booking_date: editing.booking_date || today,
        status: editing.status || "Pending",
        notes: editing.notes || "",
        booking_reference: editing.booking_reference || "",
      });
    } else {
      setForm({
        agent_id: "", client_name: "", trip_name: "", supplier_name: "",
        category: "Hotel", booking_amount: "", supplier_commission: "",
        commission_date: today, booking_date: today,
        status: "Pending", notes: "", booking_reference: "",
      });
    }
  }, [open, editing]);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    // Minimal client-side validation
    if (!form.agent_id) { toast.error("Select an agent"); return; }
    if (!form.client_name.trim()) { toast.error("Client name is required"); return; }
    if (!form.trip_name.trim()) { toast.error("Trip name is required"); return; }
    if (!form.supplier_name.trim()) { toast.error("Supplier is required"); return; }
    const amt = parseFloat(form.booking_amount);
    const comm = parseFloat(form.supplier_commission);
    if (isNaN(amt) || amt < 0) { toast.error("Booking amount must be ≥ 0"); return; }
    if (isNaN(comm) || comm < 0) { toast.error("Supplier commission must be ≥ 0"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        booking_amount: amt,
        supplier_commission: comm,
      };
      if (editing) {
        await api.put(`/admin/manual-sales/${editing.id}`, payload);
        toast.success("Manual sale updated");
      } else {
        await api.post(`/admin/manual-sales`, payload);
        toast.success("Manual sale created");
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ink-surface border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-sale-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{editing ? "Edit" : "Add"} manual sale</DialogTitle>
          <DialogDescription className="text-white/60 font-sans text-xs">
            Capture a booking that wasn't logged by the agent. This flows into Sales, Commissions, Leaderboard, and CSV exports.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Agent *</Label>
              <Select value={form.agent_id} onValueChange={(v) => set("agent_id", v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white mt-1" data-testid="manual-sale-agent"><SelectValue placeholder="Select agent" /></SelectTrigger>
                <SelectContent className="bg-ink-surface border-white/10 text-white max-h-72">
                  {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Status *</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white mt-1" data-testid="manual-sale-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ink-surface border-white/10 text-white">
                  {["Pending", "Paid", "Held", "Blocked"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Client name *</Label>
              <Input value={form.client_name} onChange={(e) => set("client_name", e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" data-testid="manual-sale-client" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Trip name *</Label>
              <Input value={form.trip_name} onChange={(e) => set("trip_name", e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" data-testid="manual-sale-trip" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Supplier *</Label>
              <Input value={form.supplier_name} onChange={(e) => set("supplier_name", e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" data-testid="manual-sale-supplier" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Category *</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white mt-1" data-testid="manual-sale-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ink-surface border-white/10 text-white">
                  {["Hotel", "Flight", "Tour", "Cruise", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Booking amount (USD) *</Label>
              <Input type="number" min="0" step="0.01" value={form.booking_amount} onChange={(e) => set("booking_amount", e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" data-testid="manual-sale-amount" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Supplier commission (USD) *</Label>
              <Input type="number" min="0" step="0.01" value={form.supplier_commission} onChange={(e) => set("supplier_commission", e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" data-testid="manual-sale-commission" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Booking date</Label>
              <Input type="date" value={form.booking_date} onChange={(e) => set("booking_date", e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" data-testid="manual-sale-booking-date" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Commission date</Label>
              <Input type="date" value={form.commission_date} onChange={(e) => set("commission_date", e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" data-testid="manual-sale-commission-date" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Booking reference (optional)</Label>
              <Input value={form.booking_reference} onChange={(e) => set("booking_reference", e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" data-testid="manual-sale-reference" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Notes (optional)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" data-testid="manual-sale-notes" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-transparent border-white/20 text-white hover:bg-white/5">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black" data-testid="manual-sale-save-btn">
            {saving ? <CircleNotch size={16} className="animate-spin" /> : (editing ? "Update" : "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// iter26 — Admin Duplicate Itinerary dialog + PDF preflight report
function DuplicateItineraryDialog({ source, open, onOpenChange, result, setResult, agents, onSuccess }) {
  const [form, setForm] = useState({ sanitize: true, new_agent_id: "", new_title: "", skip_preflight: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ sanitize: true, new_agent_id: "", new_title: "", skip_preflight: false });
    setResult && setResult(null);
  }, [open, source, setResult]);

  if (!source) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const duplicate = async () => {
    setBusy(true);
    try {
      const payload = {
        sanitize: form.sanitize,
        set_status_sent: true,
        skip_preflight: !!form.skip_preflight,
      };
      if (form.new_agent_id) payload.new_agent_id = form.new_agent_id;
      if ((form.new_title || "").trim()) payload.new_title = form.new_title.trim();
      // iter28 — allow up to 60s for the POST (preflight is hard-capped at 15s
      // server-side, so this is just a safety margin).
      const { data } = await api.post(`/admin/itineraries/${source.id}/duplicate`, payload, { timeout: 60000 });
      setResult(data);
      if (data?.preflight?.ok) {
        toast.success("Duplicate created — PDF preflight passed");
      } else if (data?.preflight?.skipped) {
        toast.success("Duplicate created — preflight skipped");
      } else {
        toast.warning("Duplicate created — but PDF preflight failed (see report). The copy is still usable.");
      }
      onSuccess && onSuccess();
    } catch (e) {
      // iter28 — surface the actual error so admin can diagnose
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (detail) msg = `Could not duplicate (${status || "?"}): ${detail}`;
      else if (e?.code === "ECONNABORTED") msg = "Duplicate timed out. Try again with 'Skip preflight' enabled.";
      else if (e?.message?.includes("Network")) msg = "Network error while duplicating. Please retry.";
      else msg = `Could not duplicate${status ? " (HTTP " + status + ")" : ""}${e?.message ? ": " + e.message : ""}`;
      toast.error(msg);
      // Log full error to console for admin debugging
      console.error("Duplicate failed:", { status, detail, error: e });
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ink-surface border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="duplicate-itinerary-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Duplicate itinerary</DialogTitle>
          <DialogDescription className="text-white/60 font-sans text-xs">
            Creates a clean sandbox copy of <span className="text-white">{source.title}</span>. Optionally sanitize HTML/emoji/broken images, then verify PDF rendering before sharing.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">New title (optional)</Label>
              <Input value={form.new_title} onChange={(e) => set("new_title", e.target.value)}
                placeholder={`Copy of ${source.title}`}
                className="bg-black/40 border-white/10 text-white mt-1" data-testid="duplicate-new-title" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Assign to agent</Label>
              <Select value={form.new_agent_id || "_keep"} onValueChange={(v) => set("new_agent_id", v === "_keep" ? "" : v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white mt-1" data-testid="duplicate-agent"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ink-surface border-white/10 text-white max-h-72">
                  <SelectItem value="_keep">Keep same agent</SelectItem>
                  {(agents || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-start gap-3 p-3 rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/5 cursor-pointer">
              <Checkbox checked={form.sanitize} onCheckedChange={(v) => set("sanitize", !!v)} data-testid="duplicate-sanitize-toggle" className="mt-0.5" />
              <span className="text-sm text-white/85 font-sans">
                <span className="block font-semibold text-white">Sanitize for PDF safety</span>
                <span className="text-xs text-white/55">Strip HTML, emoji, oversize / non-image / unreachable URLs, and any non-text gunk that may have broken the original PDF render.</span>
              </span>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-md border border-white/15 bg-white/[0.02] cursor-pointer">
              <Checkbox checked={form.skip_preflight} onCheckedChange={(v) => set("skip_preflight", !!v)} data-testid="duplicate-skip-preflight-toggle" className="mt-0.5" />
              <span className="text-sm text-white/85 font-sans">
                <span className="block font-semibold text-white">Skip PDF preflight (faster)</span>
                <span className="text-xs text-white/55">Skip the inline PDF render verification. Use this for very large itineraries (50+ images) where the preflight times out. The duplicate is still created — you can manually test the PDF afterwards.</span>
              </span>
            </label>
          </div>
        ) : (
          <div className="space-y-4 py-2" data-testid="duplicate-result">
            <div className={`rounded-lg border p-4 ${result.preflight?.ok ? "border-emerald-400/40 bg-emerald-500/5" : result.preflight?.skipped ? "border-white/15 bg-white/[0.02]" : "border-red-400/40 bg-red-500/5"}`}>
              <div className="flex items-center gap-2 mb-2">
                {result.preflight?.ok ? <CheckCircle size={18} weight="duotone" className="text-emerald-300" /> :
                 result.preflight?.skipped ? <Clock size={18} weight="duotone" className="text-white/60" /> :
                 <Prohibit size={18} weight="duotone" className="text-red-300" />}
                <p className={`text-sm font-sans font-semibold ${result.preflight?.ok ? "text-emerald-200" : result.preflight?.skipped ? "text-white/80" : "text-red-200"}`}>
                  PDF preflight {result.preflight?.ok ? "PASSED" : result.preflight?.skipped ? "SKIPPED" : "FAILED"}
                </p>
              </div>
              {!result.preflight?.skipped && (
                <div className="grid grid-cols-3 gap-3 text-[11px] text-white/70 font-sans">
                  <div><span className="text-white/40 uppercase tracking-wider text-[9px] block">HTML size</span>{(result.preflight?.html_size || 0).toLocaleString()} chars</div>
                  <div><span className="text-white/40 uppercase tracking-wider text-[9px] block">PDF size</span>{(result.preflight?.pdf_size || 0).toLocaleString()} bytes</div>
                  <div><span className="text-white/40 uppercase tracking-wider text-[9px] block">Render time</span>{result.preflight?.render_ms || 0} ms</div>
                </div>
              )}
              {result.preflight?.skipped && (
                <p className="text-xs text-white/60 font-sans">Preflight was skipped at your request — open the share link to test PDF rendering manually.</p>
              )}
              {result.preflight?.error && !result.preflight?.skipped && (
                <p className="mt-2 text-xs text-red-300 font-mono break-all" data-testid="duplicate-preflight-error">{result.preflight.error}</p>
              )}
            </div>
            <div className="rounded-lg border border-white/10 bg-black/30 p-4 space-y-2">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans">New itinerary</p>
              <p className="text-white font-serif text-lg">{result.new_title}</p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline" className="border-white/15 bg-transparent text-white/80 hover:bg-white/5 h-8" onClick={() => copyLink(result.new_share_url)} data-testid="duplicate-copy-share">
                  <Copy size={12} weight="duotone" className="mr-1" /> Share link
                </Button>
                <a href={result.new_share_url} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" className="border-white/15 bg-transparent text-white/80 hover:bg-white/5 h-8" data-testid="duplicate-open-share">
                    <ArrowSquareOut size={12} weight="duotone" className="mr-1" /> Open public view
                  </Button>
                </a>
                <Link to={`/itineraries/${result.new_id}`}>
                  <Button size="sm" className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-8" data-testid="duplicate-open-editor">
                    <PencilSimple size={12} weight="duotone" className="mr-1" /> Edit copy
                  </Button>
                </Link>
              </div>
            </div>
            {result.sanitize_changes && result.sanitize_changes.length > 0 && (
              <div className="rounded-lg border border-amber-400/30 bg-amber-500/5 p-4" data-testid="duplicate-sanitize-log">
                <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-sans mb-2">
                  Sanitized {result.sanitize_changes.length} item{result.sanitize_changes.length === 1 ? "" : "s"}
                </p>
                <ul className="space-y-0.5 text-[12px] text-white/75 font-sans">
                  {result.sanitize_changes.map((c, i) => <li key={i}>• {c}</li>)}
                </ul>
              </div>
            )}
            {result.sanitized && result.sanitize_changes?.length === 0 && (
              <p className="text-[11px] text-white/40 italic font-sans">Sanitizer ran — no changes needed. Original data was already PDF-safe.</p>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-transparent border-white/20 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={duplicate} disabled={busy} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black" data-testid="duplicate-confirm-btn">
                {busy ? <><CircleNotch size={16} className="animate-spin mr-1" /> Duplicating + preflight…</> : <><Copy size={14} weight="duotone" className="mr-1" /> Duplicate</>}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black" data-testid="duplicate-done-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// iter34 — Admin: preview all 3 PDF tiers side-by-side for a single itinerary.
// Renders Standard, Lightweight, and Emergency simultaneously in parallel and
// shows each as a downloadable preview with timing + size + result. Lets admins
// see exactly what each fallback produces — huge for diagnosing production-only
// PDF issues without redeploying.
function PdfTiersDialog({ source, open, onOpenChange }) {
  const [tiers, setTiers] = useState({ standard: null, lightweight: null, emergency: null });
  const [busy, setBusy] = useState(false);

  const renderTier = async (tier) => {
    try {
      const t0 = performance.now();
      const resp = await api.get(`/admin/itineraries/${source.id}/pdf-tier`, {
        params: { tier },
        responseType: "blob",
        timeout: 60000,
      });
      const elapsed = Math.round(performance.now() - t0);
      const url = URL.createObjectURL(resp.data);
      return {
        ok: true,
        url,
        bytes: resp.data.size,
        elapsed_ms: elapsed,
        server_ms: Number(resp.headers["x-elapsed-ms"]) || null,
        mode: resp.headers["x-pdf-mode"] || tier,
      };
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let body = "";
      // axios with responseType blob: error body is also a Blob → decode it
      try {
        if (e?.response?.data instanceof Blob) {
          body = await e.response.data.text();
          try { const j = JSON.parse(body); body = j.detail || body; } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
      return {
        ok: false,
        status: e?.response?.status,
        error: detail || body || e?.message || "render failed",
      };
    }
  };

  const runAll = async () => {
    setBusy(true);
    setTiers({ standard: null, lightweight: null, emergency: null });
    // Run all 3 tiers in parallel — same code path the public route uses
    const [std, light, emg] = await Promise.all([
      renderTier("standard"),
      renderTier("lightweight"),
      renderTier("emergency"),
    ]);
    setTiers({ standard: std, lightweight: light, emergency: emg });
    setBusy(false);
  };

  useEffect(() => {
    // Auto-run when opened
    if (open && source?.id) {
      runAll();
    }
    // Clean up blob URLs on close to avoid memory leaks
    if (!open) {
      Object.values(tiers).forEach((t) => { if (t?.url) URL.revokeObjectURL(t.url); });
      setTiers({ standard: null, lightweight: null, emergency: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source?.id]);

  if (!source) return null;
  const tierMeta = [
    { key: "standard", label: "Tier 1 · Standard", sub: "Full template + remote images. What clients normally get.", color: "#A8872C" },
    { key: "lightweight", label: "Tier 2 · Lightweight", sub: "Full template, all images stripped. Used when standard hangs on image fetch.", color: "#6FBF73" },
    { key: "emergency", label: "Tier 3 · Emergency", sub: "Content-rich text template, no images, no QR, no remote fetches. Last-resort.", color: "#3B82F6" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ink-surface border-white/10 text-white max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="pdf-tiers-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Preview all 3 PDF tiers</DialogTitle>
          <DialogDescription className="text-white/60 font-sans text-xs">
            Renders <span className="text-white">{source.title}</span> using each fallback tier in parallel.
            Confirms exactly what your clients would receive in every render path. Times shown are end-to-end
            (client + server), so they include network. Server-only timing is below each card.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between py-2">
          <div className="text-xs text-white/55 font-sans">
            Itinerary ID: <span className="text-white/80 font-mono">{source.id?.slice(0, 12)}…</span>
            {source.share_token && (
              <> · token: <span className="text-white/80 font-mono">{source.share_token}</span></>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={runAll}
            className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 text-xs uppercase tracking-widest font-sans"
            data-testid="pdf-tiers-rerun-btn"
          >
            {busy ? <><CircleNotch size={13} className="animate-spin mr-1.5" />Rendering…</> : <><ArrowsClockwise size={13} className="mr-1.5" />Re-run all</>}
          </Button>
        </div>

        <div className="space-y-3 py-2">
          {tierMeta.map((m) => {
            const t = tiers[m.key];
            return (
              <div key={m.key} className="rounded-lg border border-white/10 bg-black/30 p-4" data-testid={`pdf-tier-card-${m.key}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: m.color }} />
                      <span className="text-[11px] tracking-[0.25em] uppercase font-sans" style={{ color: m.color }}>{m.label}</span>
                    </div>
                    <div className="text-xs text-white/55 font-sans mb-3">{m.sub}</div>

                    {t === null && (
                      <div className="text-xs text-white/40 font-sans italic flex items-center gap-2">
                        <CircleNotch size={12} className="animate-spin" /> Rendering…
                      </div>
                    )}
                    {t && t.ok && (
                      <div className="flex flex-wrap gap-3 text-xs font-sans">
                        <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                          OK · {(t.bytes / 1024).toFixed(1)} KB
                        </span>
                        <span className="text-white/65">
                          server <span className="text-white/90 font-mono">{t.server_ms ?? "?"} ms</span>
                        </span>
                        <span className="text-white/65">
                          end-to-end <span className="text-white/90 font-mono">{t.elapsed_ms} ms</span>
                        </span>
                        <span className="text-white/65">
                          X-PDF-Mode <span className="text-white/90 font-mono">{t.mode}</span>
                        </span>
                      </div>
                    )}
                    {t && !t.ok && (
                      <div className="text-xs font-sans">
                        <span className="px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40">
                          FAILED · HTTP {t.status || "?"}
                        </span>
                        <div className="mt-2 p-2 rounded bg-rose-500/5 border border-rose-500/20 text-rose-200/90 font-mono text-[11px] whitespace-pre-wrap break-all">
                          {t.error}
                        </div>
                      </div>
                    )}
                  </div>

                  {t && t.ok && (
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <a
                        href={t.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center px-3 h-8 text-[11px] uppercase tracking-widest font-sans rounded border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                        data-testid={`pdf-tier-open-${m.key}`}
                      >
                        <Eye size={13} className="mr-1.5" /> Open
                      </a>
                      <a
                        href={t.url}
                        download={`${source.title || "itinerary"} - ${m.key.toUpperCase()}.pdf`}
                        className="inline-flex items-center justify-center px-3 h-8 text-[11px] uppercase tracking-widest font-sans rounded border border-white/15 text-white/80 hover:bg-white/5"
                        data-testid={`pdf-tier-download-${m.key}`}
                      >
                        <DownloadSimple size={13} className="mr-1.5" /> Save
                      </a>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/70 hover:text-white" data-testid="pdf-tiers-close-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// iter31 — Admin Research Cleanup tab.
// iter38 — Moved off the main admin dashboard tabs onto a dedicated
// `/admin/maintenance` page. Exported so AdminMaintenance.jsx can mount it.
export function AdminCleanupTab() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [showRaw, setShowRaw] = useState(null); // itin_id whose findings to display in dialog

  const load = async () => {
    setData(null);
    try {
      const { data } = await api.get("/admin/research-cleanup/preview");
      setData(data);
      setSelected(new Set());
      setExpanded(new Set());
    } catch (e) {
      setData({ error: e.response?.data?.detail || "Could not load preview" });
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (id, setRef) => {
    setRef((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelect = (id) => toggle(id, setSelected);
  const toggleExpand = (id) => toggle(id, setExpanded);

  const selectAll = () => setSelected(new Set(data.reports.map((r) => r.itinerary_id)));
  const clearAll = () => setSelected(new Set());

  const migrate = async (idsParam, dryRun) => {
    if (!data || !data.reports?.length) return;
    const ids = idsParam ? Array.from(idsParam) : [];
    const label = ids.length ? `${ids.length} selected itinerar${ids.length === 1 ? "y" : "ies"}` : `all ${data.reports.length} itineraries`;
    if (!dryRun && !window.confirm(`Migrate ${label}?\n\nOverflow text will be moved into "Internal Notes". Share links remain unchanged. This cannot be undone.`)) return;
    setBusy(true);
    const toastId = toast.loading(`${dryRun ? "Previewing" : "Migrating"} ${label}…`);
    try {
      const { data: result } = await api.post("/admin/research-cleanup/commit", {
        itinerary_ids: ids,
        dry_run: !!dryRun,
      }, { timeout: 90000 });
      if (dryRun) {
        toast.success(`Dry run: would migrate ${result.itineraries_migrated} itinerar${result.itineraries_migrated === 1 ? "y" : "ies"} (${result.total_field_moves} field moves)`, { id: toastId });
      } else {
        toast.success(`Migrated ${result.itineraries_migrated} itinerar${result.itineraries_migrated === 1 ? "y" : "ies"} — ${result.total_field_moves} field moves. Share links preserved.`, { id: toastId });
        await load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not run cleanup", { id: toastId });
    } finally {
      setBusy(false);
    }
  };

  if (data === null) return <div className="py-20 flex justify-center"><CircleNotch size={28} className="text-[#D4AF37] animate-spin" /></div>;
  if (data?.error) return <div className="border border-red-400/30 bg-red-500/5 rounded-lg p-6 text-red-200 font-sans text-sm" data-testid="cleanup-error">{data.error}</div>;

  const reports = data.reports || [];
  const totals = data.totals || {};
  const allClean = reports.length === 0;
  const rawReport = showRaw && reports.find((r) => r.itinerary_id === showRaw);

  return (
    <div className="space-y-6" data-testid="admin-cleanup-panel">
      {/* Intro */}
      <div className="rounded-lg border border-[#D4AF37]/20 bg-gradient-to-br from-[#D4AF37]/5 to-transparent p-5">
        <div className="flex items-start gap-3">
          <Broom size={22} weight="duotone" className="text-[#D4AF37] mt-0.5" />
          <div>
            <h2 className="font-serif text-2xl text-white">Research Cleanup</h2>
            <p className="text-sm text-white/65 font-sans mt-1 leading-relaxed">
              Scan every itinerary for pasted research, Google docs, or long content in client-facing fields. Overflow text gets moved into <span className="text-[#D4AF37]">Internal Notes</span> (agent-only) so the public itinerary and PDF stay clean. <strong className="text-white/90">Share links are preserved.</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="border border-white/10 bg-ink-surface rounded-lg p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Total itineraries</p>
          <p className="font-serif text-3xl text-white mt-1" data-testid="cleanup-total">{totals.itineraries_total || 0}</p>
        </div>
        <div className={`border rounded-lg p-5 ${(totals.itineraries_over || 0) > 0 ? "border-amber-400/40 bg-amber-500/5" : "border-emerald-400/30 bg-emerald-500/5"}`}>
          <p className="text-[10px] uppercase tracking-[0.3em] font-sans" style={{ color: (totals.itineraries_over || 0) > 0 ? "#FCD34D" : "#6EE7B7" }}>Need cleanup</p>
          <p className="font-serif text-3xl text-white mt-1" data-testid="cleanup-over-count">{totals.itineraries_over || 0}</p>
        </div>
        <div className="border border-white/10 bg-ink-surface rounded-lg p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Field overflows</p>
          <p className="font-serif text-3xl text-white mt-1">{totals.fields_over || 0}</p>
        </div>
        <div className="border border-white/10 bg-ink-surface rounded-lg p-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans">Excess chars</p>
          <p className="font-serif text-3xl text-white mt-1">{(totals.overflow_chars || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Action bar */}
      {!allClean && (
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" onClick={selectAll} className="border-white/15 bg-transparent text-white/80 hover:bg-white/5 h-9 text-xs uppercase tracking-widest" data-testid="cleanup-select-all">
            Select all
          </Button>
          <Button variant="outline" onClick={clearAll} className="border-white/15 bg-transparent text-white/80 hover:bg-white/5 h-9 text-xs uppercase tracking-widest" data-testid="cleanup-clear">
            Clear
          </Button>
          <Button
            variant="outline"
            onClick={() => migrate(selected.size ? selected : null, true)}
            disabled={busy}
            className="border-white/15 bg-transparent text-white/80 hover:bg-white/5 h-9 text-xs uppercase tracking-widest"
            data-testid="cleanup-dry-run-btn"
          >
            Dry run
          </Button>
          <Button
            onClick={() => migrate(selected.size ? selected : null, false)}
            disabled={busy}
            className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-9 text-xs uppercase tracking-widest"
            data-testid="cleanup-migrate-btn"
          >
            {busy ? <CircleNotch size={14} className="animate-spin mr-1" /> : <Broom size={14} weight="duotone" className="mr-1" />}
            {selected.size ? `Migrate ${selected.size} selected` : `Migrate all (${reports.length})`}
          </Button>
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/5 h-9 ml-auto" data-testid="cleanup-refresh">
            <ArrowsClockwise size={14} weight="duotone" className="mr-1" /> Refresh
          </Button>
        </div>
      )}

      {/* Empty state */}
      {allClean && (
        <div className="border border-emerald-400/30 bg-emerald-500/5 rounded-lg p-12 text-center" data-testid="cleanup-empty">
          <CheckCircle size={36} weight="duotone" className="text-emerald-300 mx-auto mb-3" />
          <p className="font-serif text-2xl text-white">All clean</p>
          <p className="text-sm text-white/60 font-sans mt-1">No itineraries have overflowing client-facing fields. Your PDFs stay light and clients see only what they should.</p>
        </div>
      )}

      {/* Itinerary list */}
      {!allClean && (
        <div className="space-y-2" data-testid="cleanup-list">
          {reports.map((r) => {
            const isSel = selected.has(r.itinerary_id);
            const isOpen = expanded.has(r.itinerary_id);
            return (
              <div key={r.itinerary_id} className={`border rounded-lg overflow-hidden transition ${isSel ? "border-[#D4AF37]/50 bg-[#D4AF37]/[0.03]" : "border-white/10 bg-ink-surface"}`} data-testid={`cleanup-row-${r.itinerary_id}`}>
                <div className="flex items-center gap-3 p-4">
                  <Checkbox checked={isSel} onCheckedChange={() => toggleSelect(r.itinerary_id)} data-testid={`cleanup-check-${r.itinerary_id}`} />
                  <button type="button" onClick={() => toggleExpand(r.itinerary_id)} className="flex items-center gap-2 flex-1 text-left group">
                    {isOpen ? <CaretDown size={14} weight="bold" className="text-white/50" /> : <CaretRight size={14} weight="bold" className="text-white/50" />}
                    <div className="flex-1">
                      <p className="text-white font-sans">{r.title || "(untitled)"}</p>
                      <p className="text-[11px] text-white/40 font-sans mt-0.5">{r.agent_name || "—"} · {r.counts.fields_over} field{r.counts.fields_over === 1 ? "" : "s"} over cap · {r.counts.overflow_chars.toLocaleString()} excess chars</p>
                    </div>
                  </button>
                  <Link to={`/itineraries/${r.itinerary_id}`} className="text-[#D4AF37] hover:text-[#F3C853] text-xs uppercase tracking-widest inline-flex items-center gap-1 font-sans" data-testid={`cleanup-edit-${r.itinerary_id}`}>
                    <PencilSimple size={12} weight="duotone" /> Edit
                  </Link>
                </div>
                {isOpen && (
                  <div className="border-t border-white/10 bg-black/30 p-4 space-y-3">
                    {r.findings.map((f, i) => (
                      <div key={i} className="space-y-1.5">
                        <div className="flex items-center justify-between gap-2 text-[11px]">
                          <span className="text-[#D4AF37] font-mono">{f.path}</span>
                          <span className="text-white/40 font-sans">{f.length.toLocaleString()} chars · {f.overflow.toLocaleString()} over (cap {f.threshold.toLocaleString()})</span>
                        </div>
                        <div className="rounded border border-amber-400/15 bg-amber-500/[0.03] p-3 text-[11px] text-white/70 font-mono leading-relaxed whitespace-pre-wrap break-words">
                          <span className="text-amber-300/70">Overflow preview:</span> {f.preview_overflow}
                          {f.overflow > 200 && <span className="text-white/30"> …+{(f.overflow - 200).toLocaleString()} more chars</span>}
                        </div>
                      </div>
                    ))}
                    <Button
                      size="sm"
                      onClick={() => migrate(new Set([r.itinerary_id]), false)}
                      disabled={busy}
                      className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-8 text-[10px] uppercase tracking-widest mt-2"
                      data-testid={`cleanup-migrate-single-${r.itinerary_id}`}
                    >
                      <Broom size={12} weight="duotone" className="mr-1" /> Migrate this itinerary
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Raw report dialog (unused but available) */}
      {rawReport && <div onClick={() => setShowRaw(null)} className="fixed inset-0 bg-black/50 z-50" />}
    </div>
  );
}


export default function AdminDashboard() {
  const [tab, setTab] = useState("itineraries");
  const [itineraries, setItineraries] = useState(null);
  const [users, setUsers] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [openUser, setOpenUser] = useState(false);
  const [userDraft, setUserDraft] = useState({ email: "", name: "", role: "agent" });
  const [savingUser, setSavingUser] = useState(false);

  // Action dialogs
  const [editUser, setEditUser] = useState(null); // user object being edited
  const [editDraft, setEditDraft] = useState({ name: "", email: "", role: "agent" });
  const [savingEdit, setSavingEdit] = useState(false);

  const [resetUser, setResetUser] = useState(null); // user object for password reset
  const [resettingPw, setResettingPw] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Status correction dialog
  const [correctingItin, setCorrectingItin] = useState(null);
  // iter26 — Duplicate itinerary dialog
  const [duplicateSource, setDuplicateSource] = useState(null);
  // iter34 — Preview all 3 PDF tiers
  const [pdfTiersSource, setPdfTiersSource] = useState(null);  const [duplicateResult, setDuplicateResult] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);

  // Team-tab filters
  const [teamSearch, setTeamSearch] = useState("");
  const [availabilityFilter, setAvailabilityFilter] = useState("all");
  const [timelineFilter, setTimelineFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Onboarding & lifecycle dialogs
  const [onboardingUser, setOnboardingUser] = useState(null); // user being managed
  const [savingOnboarding, setSavingOnboarding] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null); // { user, nextStatus }
  const [statusReason, setStatusReason] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);

  const setAgentStatus = async (user, nextStatus, reason = "") => {
    setSavingStatus(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/status`, { status: nextStatus, reason: reason || undefined });
      setUsers((prev) => (prev || []).map((u) => (u.id === user.id ? data : u)));
      toast.success(`${user.name || user.email} is now ${nextStatus}`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update status");
      return false;
    } finally {
      setSavingStatus(false);
    }
  };

  const submitOnboardingChange = async (key, value) => {
    if (!onboardingUser) return;
    setSavingOnboarding(true);
    try {
      const { data } = await api.put(`/admin/users/${onboardingUser.id}/onboarding`, { [key]: value });
      setOnboardingUser(data);
      setUsers((prev) => (prev || []).map((u) => (u.id === data.id ? data : u)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update item");
    } finally {
      setSavingOnboarding(false);
    }
  };

  const submitOnboardingNotes = async (notes) => {
    if (!onboardingUser) return;
    try {
      const { data } = await api.put(`/admin/users/${onboardingUser.id}/onboarding`, { notes });
      setOnboardingUser(data);
      setUsers((prev) => (prev || []).map((u) => (u.id === data.id ? data : u)));
      toast.success("Notes saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save notes");
    }
  };

  const loadAll = async () => {
    try {
      const [i, u, r] = await Promise.all([
        api.get("/itineraries"),
        api.get("/admin/users"),
        api.get("/admin/reviews"),
      ]);
      setItineraries(i.data);
      setUsers(u.data);
      setReviews(r.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const createUser = async (e) => {
    e.preventDefault();
    setSavingUser(true);
    try {
      await api.post("/admin/users", userDraft);
      toast.success(`${userDraft.role === "admin" ? "Admin" : "Agent"} invited — set-password email sent`);
      setOpenUser(false);
      setUserDraft({ email: "", name: "", role: "agent" });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create user");
    } finally {
      setSavingUser(false);
    }
  };

  const deleteUser = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User removed");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const updateUserRole = async (id, role) => {
    try {
      await api.put(`/admin/users/${id}`, { role });
      toast.success("Role updated");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update role");
    }
  };

  const openEditDialog = (u) => {
    setEditUser(u);
    setEditDraft({ name: u.name || "", email: u.email || "", role: u.role || "agent" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const body = {};
      if (editDraft.name !== editUser.name) body.name = editDraft.name;
      if (editDraft.email !== editUser.email) body.email = editDraft.email;
      if (editDraft.role !== editUser.role) body.role = editDraft.role;
      if (Object.keys(body).length === 0) {
        toast("No changes to save");
        setEditUser(null);
        return;
      }
      await api.put(`/admin/users/${editUser.id}`, body);
      toast.success("Profile updated");
      setEditUser(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSavingEdit(false);
    }
  };

  const openResetDialog = (u) => {
    setResetUser(u);
    setResetSent(false);
  };

  const submitResetPassword = async (e) => {
    e.preventDefault();
    setResettingPw(true);
    try {
      await api.post(`/admin/users/${resetUser.id}/reset-password`);
      setResetSent(true);
      toast.success("Password-reset email sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reset email");
    } finally {
      setResettingPw(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/toggle-active`);
      toast.success(data.is_active ? "Account activated" : "Account disabled");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Could not copy"); }
  };

  const approveReview = async (id) => {
    try {
      await api.post(`/admin/reviews/${id}/approve`);
      toast.success("Review approved");
      loadAll();
    } catch { toast.error("Failed to approve"); }
  };

  const hideReview = async (id) => {
    try {
      await api.post(`/admin/reviews/${id}/hide`);
      toast.success("Review hidden");
      loadAll();
    } catch { toast.error("Failed to hide"); }
  };

  const deleteReview = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted");
      loadAll();
    } catch { toast.error("Failed to delete review"); }
  };

  return (
    <AgentLayout>
      <div className="mb-10 animate-fade-up">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-3 font-sans">Control Room</p>
            <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-white mb-3">
              Admin <span className="italic text-[#D4AF37]">dashboard</span>
            </h1>
            <p className="text-white/60 text-sm max-w-xl font-sans">
              Oversee every journey, agent, and review across Best Time Travel &amp; Tours.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2">
            <Link
              to="/admin/email-marketing"
              data-testid="admin-email-marketing-link"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.25em] text-white/40 hover:text-[#D4AF37] transition-colors font-sans"
              title="Email marketing and subscriber database"
            >
              <EnvelopeSimple size={13} weight="duotone" /> Email marketing →
            </Link>
            <Link
              to="/admin/maintenance"
              data-testid="admin-maintenance-link"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.25em] text-white/40 hover:text-[#D4AF37] transition-colors font-sans"
              title="Research Cleanup and other admin maintenance tools"
            >
              <Broom size={13} weight="duotone" /> Maintenance tools →
            </Link>
          </div>
        </div>
      </div>

      <AdminOverviewCards />

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-ink-surface border border-white/10 p-1 h-auto flex flex-wrap gap-1 mb-8">
          <TabsTrigger value="itineraries" data-testid="admin-tab-itineraries" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <Suitcase size={14} weight="duotone" className="mr-1.5" /> Itineraries {itineraries && <span className="ml-2 text-white/40">({itineraries.length})</span>}
          </TabsTrigger>
          <TabsTrigger value="agents" data-testid="admin-tab-agents" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <Users size={14} weight="duotone" className="mr-1.5" /> Team {users && <span className="ml-2 text-white/40">({users.length})</span>}
          </TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <Star size={14} weight="duotone" className="mr-1.5" /> Reviews {reviews && <span className="ml-2 text-white/40">({reviews.length})</span>}
          </TabsTrigger>
          <TabsTrigger value="sales" data-testid="admin-tab-sales" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <ChartLineUp size={14} weight="duotone" className="mr-1.5" /> Sales
          </TabsTrigger>
          <TabsTrigger value="commissions" data-testid="admin-tab-commissions" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <Storefront size={14} weight="duotone" className="mr-1.5" /> Commissions
          </TabsTrigger>
          <TabsTrigger value="referrals" data-testid="admin-tab-referrals" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <Gift size={14} weight="duotone" className="mr-1.5" /> Referrals
          </TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="admin-tab-suppliers" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <Storefront size={14} weight="duotone" className="mr-1.5" /> Suppliers
          </TabsTrigger>
          {/* iter42 — Email Marketing nav. Rendered as a Link so clicking it
              routes to /admin/email-marketing (richer UX than cramming the full
              CRM into a tab panel inside the dashboard). Styled identically to
              the other TabsTriggers for visual consistency. */}
          <Link
            to="/admin/email-marketing"
            data-testid="admin-tab-email-marketing"
            className="inline-flex items-center text-white/60 hover:text-[#D4AF37] px-4 py-2 text-xs uppercase tracking-widest font-sans transition-colors"
          >
            <EnvelopeSimple size={14} weight="duotone" className="mr-1.5" /> Email
          </Link>
        </TabsList>

        {/* ITINERARIES */}
        <TabsContent value="itineraries">
          {itineraries === null ? (
            <div className="py-20 flex justify-center"><CircleNotch size={28} className="text-[#D4AF37] animate-spin" /></div>
          ) : itineraries.length === 0 ? (
            <div className="border border-dashed border-white/10 rounded-lg p-16 text-center text-white/50 font-sans">No itineraries yet.</div>
          ) : (
            <div className="border border-white/10 rounded-lg overflow-hidden" data-testid="admin-itineraries-table">
              <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-ink-surface text-[10px] uppercase tracking-widest text-white/50 border-b border-white/10 font-sans">
                <div className="col-span-4">Trip</div>
                <div className="col-span-3">Client</div>
                <div className="col-span-2">Agent</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {itineraries.map((it) => {
                const agent = (users || []).find((u) => u.id === it.agent_id);
                return (
                  <div key={it.id} className="grid grid-cols-12 gap-4 px-5 py-4 bg-ink text-sm hover:bg-ink-surface transition-colors items-center border-b border-white/5" data-testid={`admin-itin-row-${it.id}`}>
                    <div className="col-span-4">
                      <p className="text-white font-serif text-lg truncate flex items-center gap-2">
                        {it.title}
                        {it.is_template && (
                          <span className="text-[8px] uppercase tracking-[0.25em] px-2 py-0.5 rounded bg-[#D4AF37] text-black font-sans font-bold shrink-0">
                            Template
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-white/40 flex items-center gap-1 font-sans"><MapPin size={12} weight="duotone" /> {it.destination || "—"}</p>
                    </div>
                    <div className="col-span-3 text-white/80 truncate">{it.client_name || "—"}</div>
                    <div className="col-span-2 text-white/60 truncate">{agent?.name || "—"}</div>
                    <div className="col-span-1"><StatusBadge status={it.status || "Draft"} /></div>
                    <div className="col-span-2 text-right flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setCorrectingItin(it)}
                        className="text-white/70 hover:text-amber-300 hover:bg-amber-500/10 h-8 px-2 text-[11px] uppercase tracking-widest font-sans"
                        title={it.status === "Booked" ? "Undo Booked / change status" : "Correct status"}
                        data-testid={`admin-itin-correct-${it.id}`}
                      >
                        <ArrowsClockwise size={13} weight="duotone" className="mr-1" />
                        {it.status === "Booked" ? "Undo Booked" : "Status"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPdfTiersSource(it)}
                        className="text-white/70 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 px-2 text-[11px] uppercase tracking-widest font-sans"
                        title="Preview Standard / Lightweight / Emergency PDF tiers side-by-side"
                        data-testid={`admin-itin-pdf-tiers-${it.id}`}
                      >
                        <FilePdf size={13} weight="duotone" className="mr-1" />
                        PDF Tiers
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDuplicateSource(it)}
                        className="text-white/70 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 px-2 text-[11px] uppercase tracking-widest font-sans"
                        title="Duplicate into a clean PDF-safe copy"
                        data-testid={`admin-itin-duplicate-${it.id}`}
                      >
                        <Copy size={13} weight="duotone" className="mr-1" />
                        Duplicate
                      </Button>
                      <Link to={`/itineraries/${it.id}`}>
                        <Button size="sm" variant="ghost" className="text-white/70 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10">
                          <PencilSimple size={15} weight="duotone" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* AGENTS */}
        <TabsContent value="agents">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <Input
              placeholder="Search name, email, phone…"
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
              className="bg-black/40 border-white/10 text-white flex-1"
              data-testid="team-search"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white w-full sm:w-44" data-testid="team-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent className="bg-ink-surface border-white/10 text-white">
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={availabilityFilter} onValueChange={setAvailabilityFilter}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white w-full sm:w-44" data-testid="team-availability-filter"><SelectValue placeholder="All availability" /></SelectTrigger>
              <SelectContent className="bg-ink-surface border-white/10 text-white">
                <SelectItem value="all">All availability</SelectItem>
                <SelectItem value="full_time">Full-time</SelectItem>
                <SelectItem value="part_time">Part-time</SelectItem>
                <SelectItem value="flexible">Flexible</SelectItem>
                <SelectItem value="__none__">Not specified</SelectItem>
              </SelectContent>
            </Select>
            <Select value={timelineFilter} onValueChange={setTimelineFilter}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white w-full sm:w-44" data-testid="team-timeline-filter"><SelectValue placeholder="All timelines" /></SelectTrigger>
              <SelectContent className="bg-ink-surface border-white/10 text-white">
                <SelectItem value="all">All timelines</SelectItem>
                <SelectItem value="immediate">Immediately</SelectItem>
                <SelectItem value="1_2_weeks">Within 1–2 weeks</SelectItem>
                <SelectItem value="exploring">Just exploring</SelectItem>
                <SelectItem value="__none__">Not specified</SelectItem>
              </SelectContent>
            </Select>
            <Dialog open={openUser} onOpenChange={setOpenUser}>
              <DialogTrigger asChild>
                <Button className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest h-10 px-5 sm:shrink-0" data-testid="admin-add-user-btn">
                  <Plus size={16} weight="bold" /> Add team member
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-ink-surface border-white/10 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-serif text-3xl">
                    New <span className="italic text-[#D4AF37]">team member</span>
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={createUser} className="space-y-4 mt-2">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Full name</Label>
                    <Input required value={userDraft.name} onChange={(e) => setUserDraft({ ...userDraft, name: e.target.value })} className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="user-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Email</Label>
                    <Input required type="email" value={userDraft.email} onChange={(e) => setUserDraft({ ...userDraft, email: e.target.value })} className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="user-email-input" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Role</Label>
                    <Select value={userDraft.role} onValueChange={(v) => setUserDraft({ ...userDraft, role: v })}>
                      <SelectTrigger className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-ink-surface border-white/10 text-white">
                        <SelectItem value="agent">Agent</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={savingUser} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black w-full h-11 text-xs uppercase tracking-widest" data-testid="create-user-submit">
                      {savingUser ? <CircleNotch size={18} className="animate-spin" /> : "Create"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {users === null ? (
            <div className="py-20 flex justify-center"><CircleNotch size={28} className="text-[#D4AF37] animate-spin" /></div>
          ) : (() => {
            const q = teamSearch.trim().toLowerCase();
            const filteredUsers = users.filter((u) => {
              if (statusFilter !== "all") {
                if ((u.agent_status || "active") !== statusFilter) return false;
              }
              if (availabilityFilter !== "all") {
                const want = availabilityFilter === "__none__" ? "" : availabilityFilter;
                if ((u.availability || "") !== want) return false;
              }
              if (timelineFilter !== "all") {
                const want = timelineFilter === "__none__" ? "" : timelineFilter;
                if ((u.start_timeline || "") !== want) return false;
              }
              if (q) {
                const hay = `${u.name || ""} ${u.email || ""} ${u.phone || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
              }
              return true;
            });
            return (
              <div className="border border-white/10 rounded-lg overflow-hidden" data-testid="admin-users-table">
                <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-ink-surface text-[10px] uppercase tracking-widest text-white/50 border-b border-white/10 font-sans">
                  <div className="col-span-3">Name · Status</div>
                  <div className="col-span-3">Email · Phone</div>
                  <div className="col-span-2">Onboarding</div>
                  <div className="col-span-2">Availability</div>
                  <div className="col-span-1">Role</div>
                  <div className="col-span-1 text-right">Actions</div>
                </div>
                {filteredUsers.length === 0 ? (
                  <div className="p-12 text-center text-white/50 font-sans text-sm" data-testid="admin-users-empty">
                    No team members match the current filters.
                  </div>
                ) : filteredUsers.map((u) => {
                  const active = u.is_active !== false;
                  const lifecycle = u.agent_status || "active";
                  const onb = u.onboarding || {};
                  const onbItems = ["agreement_signed", "bank_info_submitted", "supplier_access_completed", "payout_verification"];
                  const onbDone = onbItems.filter((k) => onb[k]).length;
                  const onbTotal = onbItems.length;
                  const onbPct = Math.round((onbDone / onbTotal) * 100);
                  return (
                    <div key={u.id} className={`grid grid-cols-12 gap-4 px-5 py-4 text-sm hover:bg-ink-surface transition-colors items-center border-b border-white/5 ${active ? "bg-ink" : "bg-ink opacity-60"}`} data-testid={`user-row-${u.id}`}>
                      <div className="col-span-3 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white truncate font-sans">{u.name}</span>
                          {!active && <span className="text-[9px] uppercase tracking-widest text-amber-300 border border-amber-400/30 bg-amber-500/10 rounded-full px-2 py-0.5 shrink-0">Disabled</span>}
                        </div>
                        {u.role !== "admin" && (
                          <AgentStatusBadge status={lifecycle} data-testid={`user-status-${u.id}`} />
                        )}
                      </div>
                      <div className="col-span-3 min-w-0">
                        <p className="text-white/70 truncate font-sans">{u.email}</p>
                        <p className="text-[11px] text-white/40 font-mono truncate" data-testid={`user-phone-${u.id}`}>{u.phone || "—"}</p>
                      </div>
                      <div className="col-span-2">
                        {u.role === "admin" ? (
                          <span className="text-[11px] text-white/30 font-sans">N/A</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setOnboardingUser(u)}
                            className="group block w-full text-left"
                            data-testid={`user-onboarding-open-${u.id}`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`font-sans text-xs ${onbDone === onbTotal ? "text-emerald-300" : "text-white/70"}`} data-testid={`user-onboarding-progress-${u.id}`}>
                                {onbDone}/{onbTotal} verified
                              </span>
                              <ArrowSquareOut size={11} weight="duotone" className="text-white/40 group-hover:text-[#D4AF37]" />
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                              <div
                                className={`h-full transition-all ${onbDone === onbTotal ? "bg-emerald-400" : "bg-[#D4AF37]"}`}
                                style={{ width: `${onbPct}%` }}
                              />
                            </div>
                          </button>
                        )}
                      </div>
                      <div className="col-span-2 flex flex-wrap gap-1.5">
                        {u.availability ? (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#D4AF37]/90 font-sans" data-testid={`user-availability-${u.id}`}>
                            {AVAILABILITY_LABELS[u.availability] || u.availability}
                          </span>
                        ) : null}
                        {u.start_timeline ? (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border border-blue-400/30 bg-blue-500/10 text-blue-300 font-sans" data-testid={`user-timeline-${u.id}`}>
                            {TIMELINE_LABELS[u.start_timeline] || u.start_timeline}
                          </span>
                        ) : null}
                        {!u.availability && !u.start_timeline && (
                          <span className="text-[11px] text-white/30 font-sans">—</span>
                        )}
                      </div>
                      <div className="col-span-1">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${u.role === "admin" ? "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40" : "bg-white/5 text-white/70 border-white/10"}`}>
                          {u.role}
                        </span>
                      </div>
                      <div className="col-span-1 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" className="text-white/70 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 w-8 p-0" data-testid={`user-actions-${u.id}`}>
                              <DotsThreeVertical size={18} weight="bold" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-ink-surface border-white/10 text-white min-w-[200px]">
                            {u.role !== "admin" && (
                              <>
                                <DropdownMenuItem onClick={() => setOnboardingUser(u)} className="text-white/80 focus:bg-[#D4AF37]/10 focus:text-[#D4AF37] cursor-pointer" data-testid={`user-onboarding-${u.id}`}>
                                  <Lifebuoy size={15} weight="duotone" className="mr-2" /> Manage onboarding
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-white/5" />
                                {lifecycle !== "active" && (
                                  <DropdownMenuItem onClick={() => setStatusTarget({ user: u, nextStatus: "active" })} className="text-emerald-300 focus:bg-emerald-500/10 focus:text-emerald-300 cursor-pointer" data-testid={`user-activate-${u.id}`}>
                                    <Play size={15} weight="duotone" className="mr-2" /> Activate agent
                                  </DropdownMenuItem>
                                )}
                                {lifecycle !== "pending" && (
                                  <DropdownMenuItem onClick={() => setStatusTarget({ user: u, nextStatus: "pending" })} className="text-amber-300 focus:bg-amber-500/10 focus:text-amber-300 cursor-pointer" data-testid={`user-set-pending-${u.id}`}>
                                    <Clock size={15} weight="duotone" className="mr-2" /> Set to pending
                                  </DropdownMenuItem>
                                )}
                                {lifecycle !== "suspended" && (
                                  <DropdownMenuItem onClick={() => setStatusTarget({ user: u, nextStatus: "suspended" })} className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer" data-testid={`user-suspend-${u.id}`}>
                                    <Prohibit size={15} weight="duotone" className="mr-2" /> Suspend agent
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator className="bg-white/5" />
                              </>
                            )}
                            <DropdownMenuItem onClick={() => openEditDialog(u)} className="text-white/80 focus:bg-[#D4AF37]/10 focus:text-[#D4AF37] cursor-pointer" data-testid={`user-edit-${u.id}`}>
                              <PencilSimple size={15} weight="duotone" className="mr-2" /> Edit Profile
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openResetDialog(u)} className="text-white/80 focus:bg-[#D4AF37]/10 focus:text-[#D4AF37] cursor-pointer" data-testid={`user-reset-${u.id}`}>
                              <Key size={15} weight="duotone" className="mr-2" /> Reset Password
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleActive(u)} className="text-white/80 focus:bg-[#D4AF37]/10 focus:text-[#D4AF37] cursor-pointer" data-testid={`user-toggle-${u.id}`}>
                              {active ? <><UserMinus size={15} weight="duotone" className="mr-2" /> Disable Login</>
                                      : <><UserPlus size={15} weight="duotone" className="mr-2" /> Enable Login</>}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-white/5" />
                            <DropdownMenuItem
                              onClick={(e) => { e.preventDefault(); setDeleteTarget(u); }}
                              className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer"
                              data-testid={`user-delete-${u.id}`}
                            >
                              <Trash size={15} weight="duotone" className="mr-2" /> Delete User
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </TabsContent>

        {/* REVIEWS */}
        <TabsContent value="reviews">
          {reviews === null ? (
            <div className="py-20 flex justify-center"><CircleNotch size={28} className="text-[#D4AF37] animate-spin" /></div>
          ) : reviews.length === 0 ? (
            <div className="border border-dashed border-white/10 rounded-lg p-16 text-center text-white/50 font-sans">No reviews yet.</div>
          ) : (
            <div className="space-y-4">
              {reviews.map((r) => (
                <article key={r.id} className="border border-[#D4AF37]/20 bg-ink-surface rounded-lg p-5" data-testid={`admin-review-${r.id}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <Stars rating={r.rating} />
                        <ReviewStatusBadge status={r.status} />
                        {!r.allow_public && <span className="text-[10px] uppercase tracking-widest text-white/40 border border-white/10 rounded-full px-2 py-0.5">Private</span>}
                      </div>
                      <p className="font-serif text-xl text-white">{r.trip_name}</p>
                      <p className="text-xs text-white/60 font-sans">
                        by <span className="text-white">{r.client_name || "Anonymous"}</span> · agent <span className="text-[#D4AF37]">{r.agent_name || "—"}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {r.status !== "approved" && (
                        <Button size="sm" onClick={() => approveReview(r.id)} className="bg-green-600/90 hover:bg-green-500 text-white text-[10px] uppercase tracking-widest h-8" data-testid={`approve-review-${r.id}`}>
                          <CheckCircle size={14} weight="duotone" /> Approve
                        </Button>
                      )}
                      {r.status !== "hidden" && (
                        <Button size="sm" variant="ghost" onClick={() => hideReview(r.id)} className="text-white/70 hover:text-amber-400 hover:bg-amber-500/10" data-testid={`hide-review-${r.id}`}>
                          <EyeSlash size={15} weight="duotone" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteReview(r.id)} className="text-white/60 hover:text-red-400 hover:bg-red-500/10">
                        <Trash size={15} weight="duotone" />
                      </Button>
                    </div>
                  </div>
                  <blockquote className="text-sm text-white/80 italic leading-relaxed border-l-2 border-[#D4AF37]/40 pl-4 font-sans">
                    “{r.testimonial}”
                  </blockquote>
                </article>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SALES */}
        <TabsContent value="sales">
          <AdminSalesTab />
        </TabsContent>

        {/* COMMISSIONS */}
        <TabsContent value="commissions">
          <AdminCommissionsTab />
        </TabsContent>

        {/* REFERRALS */}
        <TabsContent value="referrals">
          <AdminReferralsTab />
        </TabsContent>

        {/* SUPPLIERS */}
        <TabsContent value="suppliers">
          <AdminSuppliersTab />
        </TabsContent>

        {/* iter31 → iter38 — Research Cleanup moved to /admin/maintenance */}
      </Tabs>

      {/* EDIT PROFILE DIALOG */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="bg-ink-surface border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              Edit <span className="italic text-[#D4AF37]">profile</span>
            </DialogTitle>
            <DialogDescription className="text-white/50 text-xs font-sans">Update name, email, or role.</DialogDescription>
          </DialogHeader>
          {editUser && (
            <form onSubmit={saveEdit} className="space-y-4 mt-2" data-testid="edit-user-form">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Full name</Label>
                <Input required value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="edit-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Email</Label>
                <Input required type="email" value={editDraft.email} onChange={(e) => setEditDraft({ ...editDraft, email: e.target.value })} className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="edit-email-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Role</Label>
                <Select value={editDraft.role} onValueChange={(v) => setEditDraft({ ...editDraft, role: v })}>
                  <SelectTrigger className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="edit-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-ink-surface border-white/10 text-white">
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter className="pt-2 gap-2">
                <Button type="button" variant="outline" onClick={() => setEditUser(null)} className="border-white/20 bg-transparent text-white/80 hover:bg-white/5 h-11 flex-1">Cancel</Button>
                <Button type="submit" disabled={savingEdit} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-11 flex-1 text-xs uppercase tracking-widest" data-testid="edit-save-btn">
                  {savingEdit ? <CircleNotch size={18} className="animate-spin" /> : "Save changes"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* RESET PASSWORD DIALOG */}
      <Dialog open={!!resetUser} onOpenChange={(o) => !o && (setResetUser(null), setResetSent(false))}>
        <DialogContent className="bg-ink-surface border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              Reset <span className="italic text-[#D4AF37]">password</span>
            </DialogTitle>
            <DialogDescription className="text-white/50 text-xs font-sans">
              {resetUser?.email}
            </DialogDescription>
          </DialogHeader>
          {resetSent ? (
            <div className="space-y-4 pt-2" data-testid="reset-success-panel">
              <div className="rounded-md border border-green-400/30 bg-green-500/10 p-4 flex gap-3">
                <CheckCircle size={22} className="text-green-300 shrink-0 mt-0.5" weight="duotone" />
                <div>
                  <p className="text-green-300 font-serif text-xl mb-1">Reset link sent</p>
                  <p className="text-white/60 text-xs font-sans leading-relaxed">
                    A secure password-reset email has been sent to <code className="text-white">{resetUser?.email}</code>. The link expires in 60 minutes.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => { setResetUser(null); setResetSent(false); }} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black w-full h-11 text-xs uppercase tracking-widest" data-testid="reset-close-btn">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={submitResetPassword} className="space-y-4 pt-2" data-testid="reset-form">
              <p className="text-white/70 text-sm font-sans leading-relaxed">
                We'll email <code className="text-[#D4AF37]">{resetUser?.email}</code> a secure single-use link to set a new password. The link expires in <strong>60 minutes</strong>.
              </p>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setResetUser(null)} className="border-white/20 bg-transparent text-white/80 hover:bg-white/5 h-11 flex-1">Cancel</Button>
                <Button type="submit" disabled={resettingPw} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-11 flex-1 text-xs uppercase tracking-widest" data-testid="reset-submit-btn">
                  {resettingPw ? <CircleNotch size={18} className="animate-spin" /> : "Send reset link"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ONBOARDING MANAGER */}
      <Dialog open={!!onboardingUser} onOpenChange={(o) => !o && setOnboardingUser(null)}>
        <DialogContent className="bg-ink-surface border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              Onboarding · <span className="italic text-[#D4AF37]">{onboardingUser?.name || onboardingUser?.email}</span>
            </DialogTitle>
            <DialogDescription className="text-white/50 text-xs font-sans">
              Admin-only checklist. Each toggle is logged with a timestamp. Agents see read-only progress.
            </DialogDescription>
          </DialogHeader>
          {onboardingUser && (
            <div className="space-y-3 mt-2" data-testid="admin-onboarding-dialog">
              <div className="flex items-center justify-between p-3 rounded-md bg-black/30 border border-white/5">
                <span className="text-xs uppercase tracking-widest text-white/50">Current status</span>
                <AgentStatusBadge status={onboardingUser.agent_status || "active"} size="md" />
              </div>

              {[
                { key: "agreement_signed", label: "Travel Advisor Agreement signed" },
                { key: "bank_info_submitted", label: "Bank / payment details submitted" },
                { key: "supplier_access_completed", label: "Supplier access request completed" },
                { key: "payout_verification", label: "Payout Verification (W-9 / Valid ID received by email)" },
              ].map((item) => {
                const checked = !!(onboardingUser.onboarding || {})[item.key];
                const ts = (onboardingUser.onboarding || {})[`${item.key}_at`];
                return (
                  <label
                    key={item.key}
                    className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${checked ? "border-emerald-400/40 bg-emerald-500/5" : "border-white/10 bg-black/20 hover:border-[#D4AF37]/30"}`}
                    data-testid={`onb-toggle-${item.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => submitOnboardingChange(item.key, e.target.checked)}
                      disabled={savingOnboarding}
                      className="mt-1 h-4 w-4 accent-[#D4AF37] cursor-pointer"
                      data-testid={`onb-checkbox-${item.key}`}
                    />
                    <div className="flex-1">
                      <p className={`text-sm font-sans ${checked ? "text-emerald-200" : "text-white"}`}>{item.label}</p>
                      {ts && (
                        <p className="text-[10px] uppercase tracking-widest text-emerald-300/70 font-sans mt-1">
                          Verified {new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })}

              <div className="pt-2">
                <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Internal notes</Label>
                <Input
                  defaultValue={(onboardingUser.onboarding || {}).notes || ""}
                  onBlur={(e) => {
                    const v = e.target.value;
                    if (v !== ((onboardingUser.onboarding || {}).notes || "")) submitOnboardingNotes(v);
                  }}
                  placeholder="e.g. Verification call completed Apr 26"
                  className="bg-black/40 border-white/10 focus:border-[#D4AF37]"
                  data-testid="onb-notes-input"
                />
              </div>

              <DialogFooter className="gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOnboardingUser(null)}
                  className="border-white/20 bg-transparent text-white/80 hover:bg-white/5 h-11 flex-1"
                  data-testid="onb-close-btn"
                >
                  Close
                </Button>
                {onboardingUser.agent_status !== "active" && (
                  <Button
                    type="button"
                    onClick={async () => {
                      const ok = await setAgentStatus(onboardingUser, "active");
                      if (ok) setOnboardingUser(null);
                    }}
                    disabled={savingStatus}
                    className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-11 flex-1 text-xs uppercase tracking-widest"
                    data-testid="onb-activate-btn"
                  >
                    {savingStatus ? <CircleNotch size={16} className="animate-spin" /> : "Activate agent"}
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* STATUS CHANGE CONFIRMATION */}
      <AlertDialog open={!!statusTarget} onOpenChange={(o) => { if (!o) { setStatusTarget(null); setStatusReason(""); } }}>
        <AlertDialogContent className="bg-ink-surface border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">
              Set <span className="italic text-[#D4AF37]">{statusTarget?.user?.name || "agent"}</span> to {AGENT_STATUS_META[statusTarget?.nextStatus || "active"]?.label || "?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60 font-sans">
              {statusTarget?.nextStatus === "active" && "The agent will be eligible for full commission and receive an activation email."}
              {statusTarget?.nextStatus === "pending" && "The agent's commissions will be HELD until they're re-activated."}
              {statusTarget?.nextStatus === "suspended" && "The agent loses access to commissions and payouts. They'll be notified by email."}
              {statusTarget?.nextStatus === "inactive" && "The agent will be flagged as inactive (no recent activity)."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {(statusTarget?.nextStatus === "suspended" || statusTarget?.nextStatus === "pending") && (
            <div className="pt-1">
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Reason (optional, included in email)</Label>
              <Input
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                placeholder="e.g. Needs call verification"
                className="bg-black/40 border-white/10 focus:border-[#D4AF37]"
                data-testid="status-reason-input"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!statusTarget) return;
                const ok = await setAgentStatus(statusTarget.user, statusTarget.nextStatus, statusReason);
                if (ok) {
                  setStatusTarget(null);
                  setStatusReason("");
                }
              }}
              className="bg-[#D4AF37] hover:bg-[#F3C853] text-black"
              data-testid="confirm-status-btn"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* DELETE CONFIRMATION */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-ink-surface border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Are you sure you want to delete this user?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60 font-sans">
              {deleteTarget && (<>
                <span className="block text-white mb-1">{deleteTarget.name} · <span className="text-white/50">{deleteTarget.email}</span></span>
                Their itineraries will remain but be orphaned. This action cannot be undone.
              </>)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (deleteTarget) { deleteUser(deleteTarget.id); setDeleteTarget(null); } }}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-user-btn"
            >
              Delete user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status correction dialog (admin tool to undo booked / correct mistakes) */}
      <StatusCorrectionDialog
        itinerary={correctingItin}
        open={!!correctingItin}
        onOpenChange={(o) => !o && setCorrectingItin(null)}
        onSuccess={async () => {
          // Refresh itineraries so the new status flows everywhere
          try {
            const i = await api.get("/itineraries");
            setItineraries(i.data);
          } catch { /* ignore */ }
        }}
      />

      {/* iter26 — Duplicate itinerary dialog */}
      <DuplicateItineraryDialog
        source={duplicateSource}
        open={!!duplicateSource}
        onOpenChange={(o) => { if (!o) { setDuplicateSource(null); setDuplicateResult(null); } }}
        result={duplicateResult}
        setResult={setDuplicateResult}
        agents={(users || []).filter((u) => u.role === "agent")}
        onSuccess={async () => {
          try {
            const i = await api.get("/itineraries");
            setItineraries(i.data);
          } catch { /* ignore */ }
        }}
      />

      {/* iter34 — Preview all 3 PDF tiers dialog */}
      <PdfTiersDialog
        source={pdfTiersSource}
        open={!!pdfTiersSource}
        onOpenChange={(o) => { if (!o) setPdfTiersSource(null); }}
      />
    </AgentLayout>
  );
}
